import { isValidDate } from '@dolfin/utils/dates'
import type { SupabaseClient } from '@supabase/supabase-js'
import { buildComparisonRows, ComparisonRow, formatComparisonLabel } from './comparisonRows'
import {
  ActivitiesWithDeals,
  CustomRecordsWithTables,
  DealLineItemsWithJoins,
  Deals,
  InvoicesWithDeals,
  KpisWithTables,
  LineItemsWithJoins,
  VirtualTableScope
} from './custom_types'
import { Database, Enums, Tables } from './db_types'
import { isSplitAwareOwnerField } from './domains/deals/splits'
import type { CustomFieldsMap } from './formula-parser'
import DolfinDSL, {
  ACTIVITIES_COLUMNS,
  DEALS_COLUMNS,
  INVOICES_COLUMNS,
  LINE_ITEMS_COLUMNS
} from './formula-parser'
import { CalculationSnapshotSourceField, RecordData } from './types'

type MetaObject = {
  value: RecordScalar
}

type RecordMeta = Record<string, unknown>
type RecordScalar = string | number | boolean | string[] | null
type DealWithSplitChildren = Deals & { split_children?: Deals[] | null }

const INVOICE_SPLIT_AMOUNT_FIELDS = [
  'amount_adjusted',
  'amount_due',
  'issued_credit_notes',
  'issued_credit_notes_without_taxes',
  'sub_total',
  'total',
  'total_excluding_tax'
] as const

const LINE_ITEM_SPLIT_AMOUNT_FIELDS = [
  'amount',
  'discount_amount',
  'tax_amount',
  'unit_amount'
] as const

const CURRENCY_FIELD_NAMES_BY_PREFIX: Partial<Record<RecordFieldPrefix, ReadonlySet<string>>> = {
  deal: new Set(['amount']),
  invoice: new Set(INVOICE_SPLIT_AMOUNT_FIELDS),
  line_item: new Set(LINE_ITEM_SPLIT_AMOUNT_FIELDS),
  deal_line_item: new Set(['amount', 'discount_amount', 'list_unit_amount', 'unit_amount'])
}

export type RuleBackedSource = Enums<'plan_source'>
export type RecordFieldPrefix =
  | 'deal'
  | 'invoice'
  | 'line_item'
  | 'deal_line_item'
  | 'activity'
  | 'kpi'
  | 'custom_record'
export type DataChangeReason = Extract<
  Enums<'recalculation_reason'>,
  | 'deal_data_changed'
  | 'invoice_data_changed'
  | 'activity_data_changed'
  | 'kpi_data_changed'
  | 'line_item_data_changed'
  | 'deal_line_item_data_changed'
  | 'custom_record_data_changed'
>

const PROVIDER_FILTER_VARIABLES_BY_SOURCE: Record<RuleBackedSource, ReadonlySet<string>> = {
  deal: new Set(['provider', 'crm_provider', 'deal.provider', 'deal.crm_provider']),
  activity: new Set([
    'provider',
    'crm_provider',
    'activity.provider',
    'activity.crm_provider',
    'deal.provider',
    'deal.crm_provider'
  ]),
  invoice: new Set([
    'provider',
    'payroll_provider',
    'invoice.provider',
    'invoice.payroll_provider',
    'deal.provider',
    'deal.crm_provider'
  ]),
  line_item: new Set([
    'provider',
    'payroll_provider',
    'line_item.provider',
    'invoice.provider',
    'invoice.payroll_provider',
    'deal.provider',
    'deal.crm_provider'
  ]),
  deal_line_item: new Set([
    'provider',
    'crm_provider',
    'deal_line_item.provider',
    'deal_line_item.crm_provider',
    'deal.provider',
    'deal.crm_provider'
  ]),
  kpi: new Set(),
  custom_record: new Set()
}

const PROVIDER_FILTER_VARIABLES = new Set(
  Object.values(PROVIDER_FILTER_VARIABLES_BY_SOURCE).flatMap(variables => [...variables])
)

export const isProviderFilterVariable = (value: string, source?: RuleBackedSource) =>
  source
    ? PROVIDER_FILTER_VARIABLES_BY_SOURCE[source].has(value)
    : PROVIDER_FILTER_VARIABLES.has(value)

const getNativeSourceFilterFields = (source: RuleBackedSource) => {
  switch (source) {
    case 'deal':
      return new Set([...DEALS_COLUMNS, 'owner_id', 'crm_provider'])
    case 'activity':
      return new Set([...ACTIVITIES_COLUMNS, 'owner_id', 'crm_provider', 'deal.crm_provider'])
    case 'invoice':
      return new Set([
        ...INVOICES_COLUMNS,
        'payroll_provider',
        'deal.owner_id',
        'deal.crm_provider'
      ])
    case 'line_item':
      return new Set([
        ...LINE_ITEMS_COLUMNS,
        'payroll_provider',
        'deal.owner_id',
        'deal.crm_provider'
      ])
    case 'deal_line_item':
      return new Set([
        'amount',
        'product_id',
        'product_name',
        'description',
        'sku',
        'quantity',
        'unit_amount',
        'currency',
        'discount_amount',
        'list_unit_amount',
        'pricebook_entry_id',
        'service_date',
        'billing_start_date',
        'billing_end_date',
        'billing_frequency',
        'billing_term',
        'crm_provider',
        ...DEALS_COLUMNS.map(column => `deal.${column}`),
        'deal.owner_id',
        'deal.crm_provider'
      ])
    case 'kpi':
    case 'custom_record':
      return new Set(['period_start', 'profile_id'])
  }
}

const collectJsonLogicVariables = (value: unknown, variables: Set<string>) => {
  if (Array.isArray(value)) {
    value.forEach(item => collectJsonLogicVariables(item, variables))
    return
  }

  if (!isPlainObject(value)) return

  if (typeof value.var === 'string') {
    variables.add(value.var)
  } else if (Array.isArray(value.var) && typeof value.var[0] === 'string') {
    variables.add(value.var[0])
  }

  Object.values(value).forEach(item => collectJsonLogicVariables(item, variables))
}

export type SourceRecordFilterValidationResult = { ok: true } | { ok: false; variable: string }

export function validateSourceRecordFilterVariables(
  source: RuleBackedSource,
  filter: unknown,
  customFields: Record<string, Tables<'custom_fields'>>
): SourceRecordFilterValidationResult {
  const variables = new Set<string>()
  collectJsonLogicVariables(filter, variables)
  const nativeFields = getNativeSourceFilterFields(source)

  for (const variable of variables) {
    if (
      variable.startsWith('user.') ||
      variable.startsWith('period.') ||
      variable.startsWith('metric_') ||
      variable.startsWith('target_')
    ) {
      return { ok: false, variable }
    }

    const canonicalVariable = canonicalizeRecordFilterField(source, variable)
    if (
      isProviderFilterVariable(variable, source) ||
      isProviderFilterVariable(canonicalVariable, source)
    ) {
      continue
    }

    let reference: RecordFieldReference
    try {
      reference = resolveRecordFieldReference(canonicalVariable, source)
    } catch {
      return { ok: false, variable }
    }

    if (!reference) return { ok: false, variable }

    if (nativeFields.has(canonicalVariable)) {
      continue
    }

    const customFieldKey =
      reference.prefix === getRecordSourcePrefix(source)
        ? reference.fieldName
        : `${reference.prefix}.${reference.fieldName}`
    if (reference.isMeta && customFields[customFieldKey]) continue

    return { ok: false, variable }
  }

  return { ok: true }
}

type SourceCustomFieldReader = {
  getCustomFields: (
    source: Enums<'plan_source'>,
    organizationId: number,
    sourceTableId?: number
  ) => Promise<Tables<'custom_fields'>[]>
}

export async function getSourceCustomFields(
  reader: SourceCustomFieldReader,
  source: Enums<'plan_source'>,
  organizationId: number,
  sourceTableId?: number
): Promise<Record<string, Tables<'custom_fields'>>> {
  let fields = await reader.getCustomFields(source, organizationId, sourceTableId)

  if (
    source === 'invoice' ||
    source === 'activity' ||
    source === 'line_item' ||
    source === 'deal_line_item'
  ) {
    fields = [
      ...fields,
      ...(await reader.getCustomFields('deal', organizationId)).map(field => ({
        ...field,
        name: `deal.${field.name}`
      })),
      ...(source === 'line_item'
        ? (await reader.getCustomFields('invoice', organizationId)).map(field => ({
            ...field,
            name: `invoice.${field.name}`
          }))
        : [])
    ]
  }

  return Object.fromEntries(fields.map(field => [field.name, field]))
}

export type AuditTableName =
  | 'deals_audit_log'
  | 'invoices_audit_log'
  | 'activities_audit_log'
  | 'kpis_audit_log'
  | 'line_items_audit_log'
  | 'deal_line_items_audit_log'
  | 'custom_records_audit_log'

export type AuditTarget = {
  tableName: AuditTableName
  idColumn:
    | 'deal_id'
    | 'invoice_id'
    | 'activity_id'
    | 'kpi_id'
    | 'line_item_id'
    | 'deal_line_item_id'
    | 'custom_record_id'
  recordId: number
}

export type SourceRecordIdColumn = AuditTarget['idColumn']

const JSON_LOGIC_CONTAINER_KEYS = new Set([
  'conditions',
  'deal_filters',
  'expirationConditions',
  'filters'
])

function isRuleBackedSource(
  source: Enums<'plan_source'> | RuleBackedSource | null | undefined
): source is RuleBackedSource {
  return !!source && source !== 'kpi'
}

export type RecordAuditRow = {
  changed_at: string
  old_data: Record<string, unknown> | null
  new_data: Record<string, unknown> | null
} & Record<string, unknown>

export type RecordAuditMatch<TAuditRow extends RecordAuditRow = RecordAuditRow> = {
  auditRow: TAuditRow
  rows: ComparisonRow[]
}

export type RecordHistorySnapshot<
  TRecord extends Record<string, unknown> = Record<string, unknown>
> = {
  changed_at: string
  record: TRecord
}

export type RecordHistoryChange<TRecord extends Record<string, unknown> = Record<string, unknown>> =
  {
    changed_at: string
    old_record: TRecord
    new_record: TRecord
    rows: ComparisonRow[]
  }

export type RecordAuditReferenceIds = {
  dealIds: number[]
  invoiceIds: number[]
  profileIds: string[]
  statementIds: number[]
}

export type RecordAuditReferenceLookup = {
  deals: Record<
    number,
    {
      label: string
      title: string
    }
  >
  invoices: Record<number, string>
  profiles: Record<string, string>
  profileDetails?: Record<string, Pick<Tables<'profiles'>, 'id' | 'name' | 'email' | 'img_url'>>
  statements: Record<number, string>
}

export const DEFAULT_RECORD_AUDIT_IGNORED_FIELDS = new Set([
  'id',
  'organization_id',
  'created_at',
  'updated_at',
  'pending_processing',
  'status',
  'synced_at',
  'last_synced_at'
])

const RECORD_AUDIT_REFERENCE_FIELD_KEYS = new Set([
  'deal_id',
  'invoice_id',
  'owner_id',
  'profile_id',
  'statement_id'
])

const DEAL_FORMULA_COLUMNS = [
  'amount',
  'close_date',
  'crm_provider',
  'flagged',
  'stage',
  'type',
  'currency',
  'name'
]

const ACTIVITY_FORMULA_COLUMNS = [
  'activity_date',
  'status',
  'type',
  ...DEAL_FORMULA_COLUMNS.map(column => `deal.${column}`)
]

const INVOICE_FORMULA_COLUMNS = [
  'status',
  'currency',
  'due_date',
  'effective_at',
  'total',
  'total_excluding_tax',
  'amount_adjusted',
  'sub_total',
  'lines',
  'paid',
  'payment_date',
  'issued_credit_notes',
  'issued_credit_notes_without_taxes',
  'subscription_id',
  'subscription_start_date',
  'deal_invoice_order',
  ...DEAL_FORMULA_COLUMNS.map(column => `deal.${column}`)
]

const LINE_ITEM_FORMULA_COLUMNS = [
  'amount',
  'description',
  'unit_amount',
  'tax_amount',
  'tax_rate',
  'currency',
  'product_id',
  'product_name',
  'quantity',
  'discount_amount',
  ...DEAL_FORMULA_COLUMNS.map(column => `deal.${column}`),
  ...INVOICE_FORMULA_COLUMNS.map(column => `invoice.${column}`)
]

const DEAL_LINE_ITEM_FORMULA_COLUMNS = [
  'amount',
  'product_id',
  'product_name',
  'description',
  'sku',
  'quantity',
  'unit_amount',
  'currency',
  'discount_amount',
  'list_unit_amount',
  'pricebook_entry_id',
  'service_date',
  'billing_start_date',
  'billing_end_date',
  'billing_frequency',
  'billing_term',
  'crm_provider',
  ...DEAL_FORMULA_COLUMNS.map(column => `deal.${column}`)
]

const KPI_FORMULA_COLUMNS = ['period_start', 'profile_id']
const CUSTOM_RECORD_FORMULA_COLUMNS = ['record_id', 'owner_id', 'created_at', 'updated_at']

const FORMULA_COLUMNS_BY_SOURCE: Record<Enums<'plan_source'>, string[]> = {
  deal: DEAL_FORMULA_COLUMNS,
  invoice: INVOICE_FORMULA_COLUMNS,
  activity: ACTIVITY_FORMULA_COLUMNS,
  line_item: LINE_ITEM_FORMULA_COLUMNS,
  deal_line_item: DEAL_LINE_ITEM_FORMULA_COLUMNS,
  kpi: KPI_FORMULA_COLUMNS,
  custom_record: CUSTOM_RECORD_FORMULA_COLUMNS
}

const formatStatementReferenceLabel = (periodStart: string | null): string => {
  if (!periodStart) {
    return 'Statement'
  }

  const date = new Date(periodStart)
  if (Number.isNaN(date.getTime())) {
    return periodStart
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC'
  }).format(date)
}

export function buildRecordAuditChangeRows({
  oldData,
  newData,
  ignoredFields = DEFAULT_RECORD_AUDIT_IGNORED_FIELDS
}: {
  oldData: Record<string, unknown>
  newData: Record<string, unknown>
  ignoredFields?: Set<string>
}): ComparisonRow[] {
  const topLevelIgnoredFields = new Set(ignoredFields)
  topLevelIgnoredFields.add('meta')

  const topLevelRows = buildComparisonRows({
    oldData,
    newData,
    ignoredFields: topLevelIgnoredFields
  })
  const metaRows = buildRecordMetaAuditChangeRows(oldData.meta, newData.meta)
  const combinedRows = [...topLevelRows, ...metaRows].map(row => {
    if (row.key === 'frozen_columns') {
      return {
        ...row,
        label: 'Manually locked fields',
        before: formatFrozenColumnsValue(oldData.frozen_columns),
        after: formatFrozenColumnsValue(newData.frozen_columns)
      }
    }

    if (row.key === 'freeze_all') {
      return {
        ...row,
        label: 'Manual record lock',
        before: row.before === 'Yes' ? 'Locked' : 'Not locked',
        after: row.after === 'Yes' ? 'Locked' : 'Not locked'
      }
    }

    return row
  })

  const hasRealSourceChanges = combinedRows.some(
    row => row.key !== 'frozen_columns' && row.key !== 'freeze_all'
  )

  return hasRealSourceChanges
    ? combinedRows.filter(row => row.key !== 'frozen_columns' && row.key !== 'freeze_all')
    : combinedRows
}

export function collectRecordAuditReferenceIds({
  oldData,
  newData,
  rows
}: {
  oldData: Record<string, unknown>
  newData: Record<string, unknown>
  rows: ComparisonRow[]
}): RecordAuditReferenceIds {
  const dealIds = new Set<number>()
  const invoiceIds = new Set<number>()
  const profileIds = new Set<string>()
  const statementIds = new Set<number>()

  rows.forEach(row => {
    if (!RECORD_AUDIT_REFERENCE_FIELD_KEYS.has(row.key)) {
      return
    }

    ;[oldData[row.key], newData[row.key]].forEach(value => {
      if (value == null || value === '') {
        return
      }

      if (row.key === 'owner_id' || row.key === 'profile_id') {
        profileIds.add(String(value))
        return
      }

      const numericValue = Number(value)
      if (Number.isNaN(numericValue)) {
        return
      }

      if (row.key === 'deal_id') {
        dealIds.add(numericValue)
      } else if (row.key === 'invoice_id') {
        invoiceIds.add(numericValue)
      } else if (row.key === 'statement_id') {
        statementIds.add(numericValue)
      }
    })
  })

  return {
    dealIds: Array.from(dealIds).sort((left, right) => left - right),
    invoiceIds: Array.from(invoiceIds).sort((left, right) => left - right),
    profileIds: Array.from(profileIds).sort(),
    statementIds: Array.from(statementIds).sort((left, right) => left - right)
  }
}

export function extractFormulaSourceFieldKeys({
  source,
  formula
}: {
  source: Enums<'plan_source'>
  formula: string | null | undefined
}) {
  if (!formula) return []
  if (source === 'kpi') return []

  const knownColumns = new Set(FORMULA_COLUMNS_BY_SOURCE[source] ?? [])
  const candidateTokens = Array.from(
    formula.matchAll(
      /\$[a-zA-Z0-9_]+|(?:deal|invoice|activity|line_item|deal_line_item|custom_record)\.[a-zA-Z0-9_$.]+|[a-zA-Z_][a-zA-Z0-9_]*/g
    )
  ).map(match => match[0])

  const fieldKeys = new Set<string>()

  candidateTokens.forEach(token => {
    if (!token.startsWith('$') && !token.includes('.') && !knownColumns.has(token)) {
      return
    }

    try {
      const reference = resolveRecordFieldReference(token, source)

      if (reference.prefix !== source) {
        return
      }

      fieldKeys.add(reference.isMeta ? `meta.${reference.fieldName}` : reference.fieldName)
    } catch {
      return
    }
  })

  return Array.from(fieldKeys).sort()
}

export async function loadRecordAuditReferenceLookup({
  supabase,
  organizationId,
  references
}: {
  supabase: SupabaseClient<Database>
  organizationId: number
  references: RecordAuditReferenceIds
}): Promise<RecordAuditReferenceLookup> {
  type QueryRowsResult<Row> = { data: Row[]; error: null }
  type DealReferenceRow = Pick<Tables<'deals'>, 'id' | 'deal_id' | 'name'>
  type InvoiceReferenceRow = Pick<Tables<'invoices'>, 'id' | 'invoice_number' | 'invoice_id'>
  type ProfileReferenceRow = Pick<Tables<'profiles'>, 'id' | 'name' | 'email' | 'img_url'>
  type StatementReferenceRow = Pick<Tables<'statements'>, 'id' | 'period_start'>

  const [dealsResult, invoicesResult, profilesResult, statementsResult] = await Promise.all([
    references.dealIds.length > 0
      ? supabase
          .from('deals')
          .select('id, deal_id, name')
          .eq('organization_id', organizationId)
          .in('id', references.dealIds)
      : Promise.resolve({ data: [], error: null } as QueryRowsResult<DealReferenceRow>),
    references.invoiceIds.length > 0
      ? supabase
          .from('invoices')
          .select('id, invoice_number, invoice_id')
          .eq('organization_id', organizationId)
          .in('id', references.invoiceIds)
      : Promise.resolve({ data: [], error: null } as QueryRowsResult<InvoiceReferenceRow>),
    references.profileIds.length > 0
      ? supabase.from('profiles').select('id, name, email, img_url').in('id', references.profileIds)
      : Promise.resolve({ data: [], error: null } as QueryRowsResult<ProfileReferenceRow>),
    references.statementIds.length > 0
      ? supabase
          .from('statements')
          .select('id, period_start')
          .eq('organization_id', organizationId)
          .in('id', references.statementIds)
      : Promise.resolve({ data: [], error: null } as QueryRowsResult<StatementReferenceRow>)
  ])

  if (dealsResult.error) throw dealsResult.error
  if (invoicesResult.error) throw invoicesResult.error
  if (profilesResult.error) throw profilesResult.error
  if (statementsResult.error) throw statementsResult.error

  return {
    deals: (dealsResult.data ?? []).reduce(
      (map, deal) => {
        const label = deal.name ?? deal.deal_id ?? `Deal ${deal.id}`
        const title =
          deal.name && deal.deal_id
            ? `${deal.name} (${deal.deal_id})`
            : (deal.name ?? deal.deal_id ?? `Deal ${deal.id}`)

        map[deal.id] = {
          label,
          title
        }
        return map
      },
      {} as Record<
        number,
        {
          label: string
          title: string
        }
      >
    ),
    invoices: (invoicesResult.data ?? []).reduce(
      (map, invoice) => {
        map[invoice.id] = invoice.invoice_number ?? invoice.invoice_id ?? `Invoice #${invoice.id}`
        return map
      },
      {} as Record<number, string>
    ),
    profiles: (profilesResult.data ?? []).reduce(
      (map, profile) => {
        map[profile.id] = profile.name ?? profile.email ?? 'Profile'
        return map
      },
      {} as Record<string, string>
    ),
    profileDetails: (profilesResult.data ?? []).reduce(
      (map, profile) => {
        map[profile.id] = profile
        return map
      },
      {} as Record<string, ProfileReferenceRow>
    ),
    statements: (statementsResult.data ?? []).reduce(
      (map, statement) => {
        map[statement.id] = formatStatementReferenceLabel(statement.period_start)
        return map
      },
      {} as Record<number, string>
    )
  }
}

export function hydrateRecordAuditChangeRows({
  rows,
  oldData,
  newData,
  references
}: {
  rows: ComparisonRow[]
  oldData: Record<string, unknown>
  newData: Record<string, unknown>
  references: RecordAuditReferenceLookup
}): ComparisonRow[] {
  const resolveReferenceValue = (key: string, value: unknown): string | null => {
    if (value == null || value === '') {
      return null
    }

    if (key === 'owner_id' || key === 'profile_id') {
      return references.profiles[String(value)] ?? null
    }

    const numericValue = Number(value)
    if (Number.isNaN(numericValue)) {
      return null
    }

    if (key === 'deal_id') {
      return references.deals[numericValue]?.label ?? null
    }

    if (key === 'invoice_id') {
      return references.invoices[numericValue] ?? null
    }

    if (key === 'statement_id') {
      return references.statements[numericValue] ?? null
    }

    return null
  }

  return rows.map(row => {
    if (!RECORD_AUDIT_REFERENCE_FIELD_KEYS.has(row.key)) {
      return row
    }

    const beforeReference = resolveReferenceValue(row.key, oldData[row.key])
    const afterReference = resolveReferenceValue(row.key, newData[row.key])

    return {
      ...row,
      label: formatComparisonLabel(row.key.replace(/_id$/, '')),
      before: beforeReference ?? row.before,
      after: afterReference ?? row.after
    }
  })
}

export function findFirstMeaningfulRecordAuditChange<TAuditRow extends RecordAuditRow>(
  auditRows: TAuditRow[],
  ignoredFields = DEFAULT_RECORD_AUDIT_IGNORED_FIELDS
): RecordAuditMatch<TAuditRow> | null {
  for (const auditRow of auditRows) {
    if (auditRow.old_data == null || auditRow.new_data == null) {
      continue
    }

    const rows = buildRecordAuditChangeRows({
      oldData: auditRow.old_data,
      newData: auditRow.new_data,
      ignoredFields
    })

    if (rows.length > 0) {
      return { auditRow, rows }
    }
  }

  return null
}

export function findMeaningfulRecordAuditChanges<TAuditRow extends RecordAuditRow>({
  auditRows,
  getRecordId,
  ignoredFields = DEFAULT_RECORD_AUDIT_IGNORED_FIELDS
}: {
  auditRows: TAuditRow[]
  getRecordId: (auditRow: TAuditRow) => number
  ignoredFields?: Set<string>
}): Array<RecordAuditMatch<TAuditRow> & { recordId: number }> {
  const rowsByRecordId = auditRows.reduce((map, auditRow) => {
    const recordId = getRecordId(auditRow)
    const rowsForRecord = map.get(recordId) ?? []
    rowsForRecord.push(auditRow)
    map.set(recordId, rowsForRecord)
    return map
  }, new Map<number, TAuditRow[]>())

  const matches = Array.from(rowsByRecordId.entries())
    .map(([recordId, rows]) => {
      const match = findFirstMeaningfulRecordAuditChange(rows, ignoredFields)
      return match ? { recordId, ...match } : null
    })
    .filter(
      (
        match
      ): match is RecordAuditMatch<TAuditRow> & {
        recordId: number
      } => match != null
    )
    .sort((left, right) => right.auditRow.changed_at.localeCompare(left.auditRow.changed_at))

  return matches
}

export function findMeaningfulRecordHistoryChanges<
  TRecord extends Record<string, unknown> = Record<string, unknown>
>(
  historyRows: RecordHistorySnapshot<TRecord>[],
  ignoredFields = DEFAULT_RECORD_AUDIT_IGNORED_FIELDS
): RecordHistoryChange<TRecord>[] {
  if (historyRows.length < 2) {
    return []
  }

  const matches: RecordHistoryChange<TRecord>[] = []

  for (let index = 1; index < historyRows.length; index += 1) {
    const previousRow = historyRows[index - 1]
    const currentRow = historyRows[index]
    const rows = buildRecordAuditChangeRows({
      oldData: previousRow.record,
      newData: currentRow.record,
      ignoredFields
    })

    if (rows.length === 0) {
      continue
    }

    matches.push({
      changed_at: currentRow.changed_at,
      old_record: previousRow.record,
      new_record: currentRow.record,
      rows
    })
  }

  return matches
}

export function findMostRecentMeaningfulRecordAuditChange<TAuditRow extends RecordAuditRow>({
  auditRows,
  getRecordId,
  ignoredFields = DEFAULT_RECORD_AUDIT_IGNORED_FIELDS
}: {
  auditRows: TAuditRow[]
  getRecordId: (auditRow: TAuditRow) => number
  ignoredFields?: Set<string>
}): (RecordAuditMatch<TAuditRow> & { recordId: number }) | null {
  return (
    findMeaningfulRecordAuditChanges({
      auditRows,
      getRecordId,
      ignoredFields
    })[0] ?? null
  )
}

function formatFrozenColumnsValue(value: unknown): string {
  if (!Array.isArray(value) || value.length === 0) {
    return 'No manually locked fields'
  }

  const columns = value
    .map(item =>
      item && typeof item === 'object' && 'column' in item
        ? String((item as { column: unknown }).column)
        : null
    )
    .filter((column): column is string => Boolean(column))
    .map(formatComparisonLabel)

  return columns.length > 0 ? columns.join(', ') : 'Manually locked fields updated'
}

function buildRecordMetaAuditChangeRows(oldMeta: unknown, newMeta: unknown): ComparisonRow[] {
  if (oldMeta == null && newMeta == null) {
    return []
  }

  const safeOldMeta =
    oldMeta && typeof oldMeta === 'object' && !Array.isArray(oldMeta)
      ? (oldMeta as Record<string, unknown>)
      : {}
  const safeNewMeta =
    newMeta && typeof newMeta === 'object' && !Array.isArray(newMeta)
      ? (newMeta as Record<string, unknown>)
      : {}

  return buildComparisonRows({
    oldData: safeOldMeta,
    newData: safeNewMeta
  }).map(row => ({
    ...row,
    key: `meta.${row.key}`
  }))
}

type PayoutDealLike = Pick<Deals, 'id' | 'organization_id' | 'deal_id' | 'name'> | { id: number }
type PayoutInvoiceLike = {
  id: number
  organization_id?: number
  deals?: PayoutDealLike | null
}
type PayoutActivityLike = {
  id: number
  organization_id?: number
  deals?: PayoutDealLike | null
}
type PayoutKpiLike = Pick<KpisWithTables, 'id' | 'organization_id'> | { id: number }
type PayoutLineItemLike = {
  id: number
  organization_id?: number
  invoices?: PayoutInvoiceLike | null
}
type PayoutDealLineItemLike = {
  id: number
  organization_id?: number
  deals?: PayoutDealLike | null
}
type PayoutCustomRecordLike =
  | Pick<CustomRecordsWithTables, 'id' | 'organization_id' | 'owner_id'>
  | {
      id: number
      organization_id?: number
      owner_id?: string | null
    }

export type PayoutSourceRecordContext = Tables<'payouts'> & {
  deals?: PayoutDealLike | null
  invoices?: PayoutInvoiceLike | null
  activities?: PayoutActivityLike | null
  kpis?: PayoutKpiLike | null
  line_items?: PayoutLineItemLike | null
  deal_line_items?: PayoutDealLineItemLike | null
  custom_records?: PayoutCustomRecordLike | null
  compensation_rules?: { source?: Enums<'plan_source'> | null } | null
}

export type DiscrepancySourceType = Enums<'plan_source'>
export type DiscrepancyDealInfo = {
  id: number
  organization_id?: number
  deal_id?: string | null
  name?: string | null
}

export type RecordFieldReference = {
  prefix: RecordFieldPrefix
  fieldName: string
  isMeta: boolean
}

export type RelationalCustomFieldSourceRole = 'local' | 'parent_deal' | 'parent_invoice'

export type RelationalCustomFieldDescriptor = {
  source: Enums<'plan_source'>
  sourceRole: RelationalCustomFieldSourceRole
  customFieldId: Tables<'custom_fields'>['id']
  virtualTableScope: VirtualTableScope | null
}

export type RelationalCustomFieldDescriptorParams = {
  source: Enums<'plan_source'>
  field: string | RecordFieldReference
  definition: Pick<
    Tables<'custom_fields'>,
    'custom_record_table_id' | 'id' | 'kpis_table_id' | 'name' | 'table_name' | 'type'
  >
  virtualTableScope?: VirtualTableScope | null
}

export type RelationalCustomFieldQuerySource =
  | 'activity'
  | 'custom_record'
  | 'deal'
  | 'invoice'
  | 'deal_line_item'
  | 'kpi'

export type RelationalCustomFieldFilter =
  | {
      customFieldId: number
      operator: 'user_eq'
      value: string
    }
  | {
      customFieldId: number
      operator: 'date_between'
      value: { end: string; start: string }
    }

export type RelationalCustomFieldSort = {
  customFieldId: number
  direction: 'asc' | 'desc'
}

export type RelationalCustomFieldQueryInput = {
  filters?: RelationalCustomFieldFilter[]
  sort?: RelationalCustomFieldSort
}

type RelationalCustomFieldDefinition = Pick<
  Tables<'custom_fields'>,
  'custom_record_table_id' | 'id' | 'kpis_table_id' | 'name' | 'table_name' | 'type'
>

export type RelationalCustomFieldQueryValidationContext = {
  definitions: RelationalCustomFieldDefinition[]
  source: RelationalCustomFieldQuerySource
  virtualTableScope?: VirtualTableScope | null
}

export type ValidatedRelationalCustomFieldFilter = RelationalCustomFieldFilter & {
  descriptor: RelationalCustomFieldDescriptor
}

export type ValidatedRelationalCustomFieldSort = RelationalCustomFieldSort & {
  descriptor: RelationalCustomFieldDescriptor
}

export type ValidatedRelationalCustomFieldQueryInput = {
  filters: ValidatedRelationalCustomFieldFilter[]
  sort: ValidatedRelationalCustomFieldSort | null
}

export type RelationalCustomFieldQueryValidationError = {
  code: 'invalid_scope' | 'invalid_value' | 'unsupported_field' | 'unsupported_operator'
  message: string
}

export type RelationalCustomFieldQueryValidationResult =
  | { ok: true; value: ValidatedRelationalCustomFieldQueryInput }
  | { ok: false; error: RelationalCustomFieldQueryValidationError }

export type RecordExecutionAttribution = {
  item: RecordData
  sourceKey: string
  attributionKey: string
  splitChildDealId: number | null
  splitAnchorDealId: number | null
  splitPercentage: number | null
  splitOriginalAmount: number | null
  splitAllocatedAmount: number | null
}

type SourceLinkedIds = {
  activity_id?: number | null
  custom_record_id?: number | null
  deal_id?: number | null
  invoice_id?: number | null
  kpi_id?: number | null
  line_item_id?: number | null
  deal_line_item_id?: number | null
}

export type RecordDisplayLabelOptions = {
  fallbackId?: number | null
  getProfileLabel?: (profileId: string) => string | null | undefined
}

function isRecordFieldReference(
  field: string | RecordFieldReference
): field is RecordFieldReference {
  return typeof field !== 'string'
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value != null && !Array.isArray(value)
}

function hasOnlyKeys(value: Record<string, unknown>, allowedKeys: string[]) {
  return Object.keys(value).every(key => allowedKeys.includes(key))
}

function isUuid(value: string) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)
}

function getRelationalCustomFieldReferencePrefix(tableName: string): RecordFieldPrefix | null {
  switch (tableName) {
    case 'activities':
      return 'activity'
    case 'custom_records':
      return 'custom_record'
    case 'deals':
      return 'deal'
    case 'invoices':
      return 'invoice'
    case 'deal_line_items':
      return 'deal_line_item'
    case 'kpis':
      return 'kpi'
    default:
      return null
  }
}

function getRelationalCustomFieldValidationError(
  code: RelationalCustomFieldQueryValidationError['code'],
  message: string
): RelationalCustomFieldQueryValidationResult {
  return { ok: false, error: { code, message } }
}

function getValidatedRelationalCustomFieldDescriptor(
  customFieldId: unknown,
  context: RelationalCustomFieldQueryValidationContext
):
  | {
      ok: true
      definition: RelationalCustomFieldDefinition
      descriptor: RelationalCustomFieldDescriptor
    }
  | { ok: false; error: RelationalCustomFieldQueryValidationError } {
  if (typeof customFieldId !== 'number' || !Number.isInteger(customFieldId) || customFieldId <= 0) {
    return {
      ok: false,
      error: { code: 'invalid_value', message: 'customFieldId must be a positive integer' }
    }
  }

  const definition = context.definitions.find(field => field.id === customFieldId)
  if (!definition || definition.type === 'formula') {
    return {
      ok: false,
      error: { code: 'unsupported_field', message: 'Custom field is not relationally supported' }
    }
  }

  const prefix = getRelationalCustomFieldReferencePrefix(definition.table_name)
  if (!prefix) {
    return {
      ok: false,
      error: {
        code: 'unsupported_field',
        message: 'Custom field table is not relationally supported'
      }
    }
  }

  const descriptor = getRelationalCustomFieldDescriptor({
    source: context.source,
    field: { prefix, fieldName: definition.name, isMeta: true },
    definition,
    virtualTableScope: context.virtualTableScope ?? null
  })
  if (!descriptor) {
    return {
      ok: false,
      error: {
        code: 'invalid_scope',
        message: 'Custom field is outside the requested source scope'
      }
    }
  }

  return { ok: true, definition, descriptor }
}

export function parseRelationalCustomFieldQueryInput(
  input: unknown,
  context: RelationalCustomFieldQueryValidationContext
): RelationalCustomFieldQueryValidationResult {
  if (!isPlainObject(input) || !hasOnlyKeys(input, ['filters', 'sort'])) {
    return getRelationalCustomFieldValidationError(
      'invalid_value',
      'Query input must contain only semantic filters and sort descriptors'
    )
  }

  const filtersInput = input.filters ?? []
  if (!Array.isArray(filtersInput)) {
    return getRelationalCustomFieldValidationError('invalid_value', 'filters must be an array')
  }

  const filters: ValidatedRelationalCustomFieldFilter[] = []
  for (const filter of filtersInput) {
    if (!isPlainObject(filter) || !hasOnlyKeys(filter, ['customFieldId', 'operator', 'value'])) {
      return getRelationalCustomFieldValidationError(
        'invalid_value',
        'Filter must contain only customFieldId, operator, and value'
      )
    }

    const resolved = getValidatedRelationalCustomFieldDescriptor(filter.customFieldId, context)
    if (!resolved.ok) return { ok: false, error: resolved.error }

    if (resolved.definition.type === 'user' && filter.operator === 'user_eq') {
      if (typeof filter.value !== 'string' || !isUuid(filter.value)) {
        return getRelationalCustomFieldValidationError(
          'invalid_value',
          'user_eq requires a valid UUID value'
        )
      }
      filters.push({
        customFieldId: resolved.descriptor.customFieldId,
        descriptor: resolved.descriptor,
        operator: 'user_eq',
        value: filter.value
      })
      continue
    }

    if (resolved.definition.type === 'date' && filter.operator === 'date_between') {
      if (
        !isPlainObject(filter.value) ||
        !hasOnlyKeys(filter.value, ['end', 'start']) ||
        typeof filter.value.start !== 'string' ||
        typeof filter.value.end !== 'string' ||
        !isValidDate(filter.value.start) ||
        !isValidDate(filter.value.end) ||
        filter.value.start > filter.value.end
      ) {
        return getRelationalCustomFieldValidationError(
          'invalid_value',
          'date_between requires an ordered ISO date range'
        )
      }
      filters.push({
        customFieldId: resolved.descriptor.customFieldId,
        descriptor: resolved.descriptor,
        operator: 'date_between',
        value: { end: filter.value.end, start: filter.value.start }
      })
      continue
    }

    return getRelationalCustomFieldValidationError(
      'unsupported_operator',
      'Operator is unsupported for the custom field type'
    )
  }

  if (input.sort == null) {
    return { ok: true, value: { filters, sort: null } }
  }

  if (!isPlainObject(input.sort) || !hasOnlyKeys(input.sort, ['customFieldId', 'direction'])) {
    return getRelationalCustomFieldValidationError(
      'invalid_value',
      'Sort must contain only customFieldId and direction'
    )
  }

  const resolvedSort = getValidatedRelationalCustomFieldDescriptor(
    input.sort.customFieldId,
    context
  )
  if (!resolvedSort.ok) return { ok: false, error: resolvedSort.error }
  if (resolvedSort.definition.type !== 'user' && resolvedSort.definition.type !== 'date') {
    return getRelationalCustomFieldValidationError(
      'unsupported_operator',
      'Sorting is unsupported for the custom field type'
    )
  }
  if (input.sort.direction !== 'asc' && input.sort.direction !== 'desc') {
    return getRelationalCustomFieldValidationError(
      'invalid_value',
      'Sort direction must be asc or desc'
    )
  }

  return {
    ok: true,
    value: {
      filters,
      sort: {
        customFieldId: resolvedSort.descriptor.customFieldId,
        descriptor: resolvedSort.descriptor,
        direction: input.sort.direction
      }
    }
  }
}

function roundSplitAmount(value: number, percentage: number | null | undefined) {
  if (percentage == null) {
    return value
  }

  return Number(((value * percentage) / 100).toFixed(2))
}

function scaleNumericFields<T extends Record<string, unknown>>(
  record: T,
  fields: readonly string[],
  percentage: number | null | undefined
): T {
  if (percentage == null) {
    return record
  }

  return fields.reduce(
    (scaledRecord, fieldName) => {
      const currentValue = scaledRecord[fieldName]

      if (typeof currentValue === 'number') {
        scaledRecord[fieldName] = roundSplitAmount(currentValue, percentage)
      }

      return scaledRecord
    },
    { ...record } as Record<string, unknown>
  ) as T
}

function stringifyRecordFieldReference(
  source: RuleBackedSource | Enums<'plan_source'>,
  field: string | RecordFieldReference
) {
  const resolvedField = isRecordFieldReference(field)
    ? field
    : resolveRecordFieldReference(field, source as RuleBackedSource)

  const defaultPrefix = getRecordSourcePrefix(source as RuleBackedSource)
  const fieldPrefix = resolvedField.prefix === defaultPrefix ? '' : `${resolvedField.prefix}.`
  const metaPrefix = resolvedField.isMeta ? '$' : ''

  return `${fieldPrefix}${metaPrefix}${resolvedField.fieldName}`
}

export function canonicalizeRecordFilterField(source: RuleBackedSource, field: string): string {
  if (field.startsWith('user.') || field.startsWith('period.')) {
    return field
  }

  try {
    return stringifyRecordFieldReference(source, field)
  } catch {
    return field
  }
}

export function canonicalizeJsonLogicRecordVars(source: RuleBackedSource, value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(item => canonicalizeJsonLogicRecordVars(source, item))
  }

  if (!value || typeof value !== 'object') {
    return value
  }

  const nextValue = Object.fromEntries(
    Object.entries(value).map(([key, childValue]) => [
      key,
      canonicalizeJsonLogicRecordVars(source, childValue)
    ])
  ) as Record<string, unknown>

  if (typeof nextValue.var === 'string') {
    nextValue.var = canonicalizeRecordFilterField(source, nextValue.var)
  }

  return nextValue
}

export function canonicalizeSourceAwareJsonLogic(
  source: Enums<'plan_source'> | RuleBackedSource | null | undefined,
  value: unknown
): unknown {
  if (!isRuleBackedSource(source)) {
    return value
  }

  return canonicalizeJsonLogicRecordVars(source, value)
}

export function canonicalizeSourceAwareJsonLogicContainers(
  source: Enums<'plan_source'> | RuleBackedSource | null | undefined,
  value: unknown
): unknown {
  if (!isRuleBackedSource(source)) {
    return value
  }

  if (Array.isArray(value)) {
    return value.map(item => canonicalizeSourceAwareJsonLogicContainers(source, item))
  }

  if (!value || typeof value !== 'object') {
    return value
  }

  return Object.fromEntries(
    Object.entries(value).map(([key, childValue]) => [
      key,
      JSON_LOGIC_CONTAINER_KEYS.has(key)
        ? canonicalizeJsonLogicRecordVars(source, childValue)
        : canonicalizeSourceAwareJsonLogicContainers(source, childValue)
    ])
  )
}

function getProviderFieldReference(
  source: Extract<
    RuleBackedSource,
    'activity' | 'deal' | 'invoice' | 'line_item' | 'deal_line_item'
  >
): RecordFieldReference {
  switch (source) {
    case 'deal':
      return {
        prefix: 'deal',
        fieldName: 'crm_provider',
        isMeta: false
      }
    case 'invoice':
      return {
        prefix: 'invoice',
        fieldName: 'payroll_provider',
        isMeta: false
      }
    case 'activity':
      return {
        prefix: 'activity',
        fieldName: 'crm_provider',
        isMeta: false
      }
    case 'line_item':
      return {
        prefix: 'invoice',
        fieldName: 'payroll_provider',
        isMeta: false
      }
    case 'deal_line_item':
      return { prefix: 'deal_line_item', fieldName: 'crm_provider', isMeta: false }
  }
}

function getPrefixedProviderFieldReference(
  prefix: Extract<
    RecordFieldPrefix,
    'activity' | 'deal' | 'invoice' | 'line_item' | 'deal_line_item'
  >
): RecordFieldReference {
  switch (prefix) {
    case 'deal':
      return {
        prefix: 'deal',
        fieldName: 'crm_provider',
        isMeta: false
      }
    case 'invoice':
      return {
        prefix: 'invoice',
        fieldName: 'payroll_provider',
        isMeta: false
      }
    case 'activity':
      return {
        prefix: 'activity',
        fieldName: 'crm_provider',
        isMeta: false
      }
    case 'line_item':
      return {
        prefix: 'invoice',
        fieldName: 'payroll_provider',
        isMeta: false
      }
    case 'deal_line_item':
      return { prefix: 'deal_line_item', fieldName: 'crm_provider', isMeta: false }
  }
}

function normalizeLocalSourceFieldName(
  field: string | RecordFieldReference,
  prefix: 'kpi' | 'custom_record'
) {
  if (isRecordFieldReference(field)) {
    return field.prefix === prefix ? `${field.isMeta ? '$' : ''}${field.fieldName}` : null
  }

  if (field === '' || field === '$') {
    return null
  }

  if (field.startsWith(`${prefix}.$`)) {
    const normalizedField = `$${field.slice(prefix.length + 2)}`
    return normalizedField === '$' || normalizedField.includes('.') ? null : normalizedField
  }

  if (field.startsWith(`${prefix}.`)) {
    const normalizedField = field.slice(prefix.length + 1)
    return normalizedField === '' || normalizedField.includes('.') ? null : normalizedField
  }

  return field.includes('.') ? null : field
}

export function getRecordSourcePrefix(source: RuleBackedSource): RecordFieldPrefix {
  if (
    source === 'deal' ||
    source === 'invoice' ||
    source === 'line_item' ||
    source === 'deal_line_item' ||
    source === 'activity' ||
    source === 'kpi' ||
    source === 'custom_record'
  ) {
    return source
  }

  throw new Error(`Unsupported source: ${source}`)
}

export function getRecordForeignKeyField(source: Enums<'plan_source'>) {
  if (source === 'deal') {
    return 'deal_id' as const
  }

  if (source === 'activity') {
    return 'activity_id' as const
  }

  if (source === 'invoice') {
    return 'invoice_id' as const
  }

  if (source === 'line_item') {
    return 'line_item_id' as const
  }

  if (source === 'deal_line_item') {
    return 'deal_line_item_id' as const
  }

  if (source === 'custom_record') {
    return 'custom_record_id' as const
  }

  return 'kpi_id' as const
}

export function getPopulatedRecordSourceAndId(value: SourceLinkedIds): {
  source: Enums<'plan_source'> | 'period'
  sourceId: number | null
} {
  if (value.deal_id != null) {
    return { source: 'deal', sourceId: value.deal_id }
  }

  if (value.activity_id != null) {
    return { source: 'activity', sourceId: value.activity_id }
  }

  if (value.invoice_id != null) {
    return { source: 'invoice', sourceId: value.invoice_id }
  }

  if (value.line_item_id != null) {
    return { source: 'line_item', sourceId: value.line_item_id }
  }

  if (value.deal_line_item_id != null) {
    return { source: 'deal_line_item', sourceId: value.deal_line_item_id }
  }

  if (value.custom_record_id != null) {
    return { source: 'custom_record', sourceId: value.custom_record_id }
  }

  if (value.kpi_id != null) {
    return { source: 'kpi', sourceId: value.kpi_id }
  }

  return { source: 'period', sourceId: null }
}

export function resolveRecordFieldReference(
  field: string,
  source: RuleBackedSource
): RecordFieldReference {
  const defaultPrefix = getRecordSourcePrefix(source)

  if (field === 'provider') {
    return getProviderFieldReference(
      source as Extract<
        RuleBackedSource,
        'activity' | 'deal' | 'invoice' | 'line_item' | 'deal_line_item'
      >
    )
  }

  if (
    field === 'deal.provider' ||
    field === 'invoice.provider' ||
    field === 'activity.provider' ||
    field === 'line_item.provider' ||
    field === 'deal_line_item.provider'
  ) {
    return getPrefixedProviderFieldReference(
      field.split('.')[0] as Extract<
        RecordFieldPrefix,
        'activity' | 'deal' | 'invoice' | 'line_item' | 'deal_line_item'
      >
    )
  }

  if (field.startsWith('$')) {
    return {
      prefix: defaultPrefix,
      fieldName: field.slice(1),
      isMeta: true
    }
  }

  const customPrefixMatch = field.match(/^([a-z_]+)\.\$(.+)$/i)
  if (customPrefixMatch) {
    return {
      prefix: getRecordSourcePrefix(customPrefixMatch[1] as RuleBackedSource),
      fieldName: customPrefixMatch[2],
      isMeta: true
    }
  }

  const dottedMatch = field.match(/^([a-z_]+)\.([a-z_][a-z0-9_]*)$/i)
  if (dottedMatch) {
    return {
      prefix: getRecordSourcePrefix(dottedMatch[1] as RuleBackedSource),
      fieldName: dottedMatch[2],
      isMeta: false
    }
  }

  return {
    prefix: defaultPrefix,
    fieldName: field,
    isMeta: false
  }
}

/**
 * Returns the native currency field that denominates a monetary source field.
 * Custom fields and formulas intentionally return null: their output type does
 * not establish a currency or its source.
 */
export function getRecordFieldCurrencyReference(
  source: RuleBackedSource,
  field: string
): RecordFieldReference | null {
  const reference = resolveRecordFieldReference(field, source)
  const currencyFields = CURRENCY_FIELD_NAMES_BY_PREFIX[reference.prefix]

  if (reference.isMeta || !currencyFields?.has(reference.fieldName)) {
    return null
  }

  return {
    prefix: reference.prefix,
    fieldName: 'currency',
    isMeta: false
  }
}

export function getRecordFieldValue(
  source: RuleBackedSource,
  record: RecordData,
  fieldPath: string | RecordFieldReference,
  customFields?: { [index: string]: Tables<'custom_fields'> }
) {
  return new DolfinRecord(source, record).getFieldValue(fieldPath, customFields)
}

function asDisplayString(value: unknown) {
  if (typeof value === 'string' && value.trim().length > 0) {
    return value.trim()
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value)
  }

  return null
}

function cloneRecordForSplitAttribution(
  source: RuleBackedSource,
  item: RecordData,
  splitDeal: Deals
): RecordData {
  if (source === 'invoice') {
    return scaleNumericFields(
      {
        ...(item as InvoicesWithDeals),
        deals: splitDeal
      },
      INVOICE_SPLIT_AMOUNT_FIELDS,
      splitDeal.split_percentage
    ) as InvoicesWithDeals
  }

  if (source === 'activity') {
    return {
      ...(item as ActivitiesWithDeals),
      deals: splitDeal
    } as ActivitiesWithDeals
  }

  if (source === 'line_item') {
    const lineItem = item as LineItemsWithJoins

    return scaleNumericFields(
      {
        ...lineItem,
        invoices: lineItem.invoices
          ? scaleNumericFields(
              {
                ...lineItem.invoices,
                deals: splitDeal
              },
              INVOICE_SPLIT_AMOUNT_FIELDS,
              splitDeal.split_percentage
            )
          : null
      },
      LINE_ITEM_SPLIT_AMOUNT_FIELDS,
      splitDeal.split_percentage
    ) as LineItemsWithJoins
  }

  if (source === 'deal_line_item') {
    return scaleNumericFields(
      { ...(item as DealLineItemsWithJoins), deals: splitDeal },
      ['amount', 'discount_amount', 'list_unit_amount', 'unit_amount'],
      splitDeal.split_percentage
    ) as DealLineItemsWithJoins
  }

  return item
}

export function getRecordExecutionAttributions(
  source: Enums<'plan_source'>,
  item: RecordData
): RecordExecutionAttribution[] {
  if (source === 'kpi') {
    return [
      {
        item,
        sourceKey: `kpi:${item.id}`,
        attributionKey: `kpi:${item.id}`,
        splitChildDealId: null,
        splitAnchorDealId: null,
        splitPercentage: null,
        splitOriginalAmount: null,
        splitAllocatedAmount: null
      }
    ]
  }

  const record = new DolfinRecord(source, item)
  const sourceKey = `${source}:${item.id}`

  if (source === 'deal') {
    const deal = item as Deals

    if (deal.split_record_type === 'anchor') {
      return []
    }

    return [
      {
        item,
        sourceKey,
        attributionKey: sourceKey,
        splitChildDealId: deal.split_record_type === 'split_child' ? deal.id : null,
        splitAnchorDealId: deal.split_anchor_id ?? null,
        splitPercentage: deal.split_percentage ?? null,
        splitOriginalAmount: deal.split_original_amount ?? null,
        splitAllocatedAmount: deal.split_allocated_amount ?? null
      }
    ]
  }

  const parentDeal = record.getParentDeal() as DealWithSplitChildren | undefined

  if (parentDeal?.split_record_type === 'anchor' && parentDeal.split_children === undefined) {
    throw new Error('Missing split_children for split anchor deal attribution', {
      cause: {
        source,
        recordId: item.id,
        parentDealId: parentDeal.id
      }
    })
  }

  const splitChildren = parentDeal?.split_children?.filter(
    child => child.split_record_type === 'split_child'
  )

  if (parentDeal?.split_record_type === 'anchor' && splitChildren && splitChildren.length > 0) {
    return splitChildren.map(splitChild => ({
      item: cloneRecordForSplitAttribution(source, item, splitChild),
      sourceKey,
      attributionKey: `${source}:${item.id}:deal:${splitChild.id}`,
      splitChildDealId: splitChild.id,
      splitAnchorDealId: parentDeal.id,
      splitPercentage: splitChild.split_percentage ?? null,
      splitOriginalAmount: splitChild.split_original_amount ?? null,
      splitAllocatedAmount: splitChild.split_allocated_amount ?? null
    }))
  }

  if (parentDeal?.split_record_type === 'split_child') {
    return [
      {
        item,
        sourceKey,
        attributionKey: `${source}:${item.id}:deal:${parentDeal.id}`,
        splitChildDealId: parentDeal.id,
        splitAnchorDealId: parentDeal.split_anchor_id ?? null,
        splitPercentage: parentDeal.split_percentage ?? null,
        splitOriginalAmount: parentDeal.split_original_amount ?? null,
        splitAllocatedAmount: parentDeal.split_allocated_amount ?? null
      }
    ]
  }

  return [
    {
      item,
      sourceKey,
      attributionKey: sourceKey,
      splitChildDealId: null,
      splitAnchorDealId: null,
      splitPercentage: null,
      splitOriginalAmount: null,
      splitAllocatedAmount: null
    }
  ]
}

export function getMetricExecutionAttributions(
  source: Enums<'plan_source'>,
  item: RecordData,
  userField: string | null | undefined
): RecordExecutionAttribution[] {
  if (source === 'kpi' || isSplitAwareOwnerField(source, userField)) {
    return getRecordExecutionAttributions(source, item)
  }

  if (source === 'deal') {
    const deal = item as Deals
    if (deal.split_record_type === 'anchor') {
      return [
        {
          item,
          sourceKey: `deal:${item.id}`,
          attributionKey: `deal:${item.id}`,
          splitChildDealId: null,
          splitAnchorDealId: deal.id,
          splitPercentage: null,
          splitOriginalAmount: deal.split_original_amount ?? deal.amount ?? null,
          splitAllocatedAmount: null
        }
      ]
    }

    if (deal.split_record_type === 'split_child') {
      return []
    }
  }

  const parentDeal = new DolfinRecord(source, item).getParentDeal() as
    | DealWithSplitChildren
    | undefined
  if (parentDeal?.split_record_type === 'anchor') {
    return [
      {
        item,
        sourceKey: `${source}:${item.id}`,
        attributionKey: `${source}:${item.id}`,
        splitChildDealId: null,
        splitAnchorDealId: parentDeal.id,
        splitPercentage: null,
        splitOriginalAmount: parentDeal.split_original_amount ?? parentDeal.amount ?? null,
        splitAllocatedAmount: null
      }
    ]
  }

  return getRecordExecutionAttributions(source, item)
}

class DolfinRecord {
  private readonly fieldsRead = new Map<string, CalculationSnapshotSourceField>()
  static DATE_COLUMN_BY_SOURCE: Record<Enums<'plan_source'>, string> = {
    deal: 'close_date',
    activity: 'activity_date',
    invoice: 'effective_at',
    kpi: 'period_start',
    line_item: 'invoice.effective_at',
    deal_line_item: 'deal.close_date',
    custom_record: 'created_at'
  }

  static CUSTOM_FIELD_REFERENCE_PREFIX_BY_TABLE: Partial<
    Record<Tables<'custom_fields'>['table_name'], string>
  > = {
    deals: 'deal',
    activities: 'activity',
    invoices: 'invoice',
    line_items: 'line_item',
    deal_line_items: 'deal_line_item',
    kpis: 'kpi',
    custom_records: 'custom_record'
  }

  static CUSTOM_FIELD_TABLE_BY_PREFIX: Record<
    RecordFieldPrefix,
    Tables<'custom_fields'>['table_name']
  > = {
    deal: 'deals',
    activity: 'activities',
    invoice: 'invoices',
    line_item: 'line_items',
    deal_line_item: 'deal_line_items',
    kpi: 'kpis',
    custom_record: 'custom_records'
  }
  static getSourceTableId(source: Enums<'plan_source'>, record: RecordData): number | undefined {
    if (source === 'kpi' && 'kpis_table_id' in record) {
      return record.kpis_table_id ?? undefined
    }

    if (source === 'custom_record' && 'custom_record_table_id' in record) {
      return record.custom_record_table_id ?? undefined
    }

    return undefined
  }

  static escapeRegExp(value: string) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  }

  static isRecordScalar(value: unknown): value is RecordScalar {
    return (
      value === null ||
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean' ||
      (Array.isArray(value) && value.every(item => typeof item === 'string'))
    )
  }

  static getCustomFieldReferenceTokens(
    field: Pick<Tables<'custom_fields'>, 'name' | 'table_name'>
  ) {
    const prefixedToken = DolfinRecord.CUSTOM_FIELD_REFERENCE_PREFIX_BY_TABLE[field.table_name]

    return Array.from(
      new Set(
        [`$${field.name}`, prefixedToken && `${prefixedToken}.$${field.name}`].filter(
          Boolean
        ) as string[]
      )
    )
  }

  static stringReferencesCustomField(value: string, tokens: string[]) {
    return tokens.some(token => {
      if (value === token) {
        return true
      }

      return new RegExp(
        `(^|[\\s()[\\]{},+\\-*/.:;!?=<>%&|^])${DolfinRecord.escapeRegExp(
          token
        )}($|[\\s()[\\]{},+\\-*/.:;!?=<>%&|^])`
      ).test(value)
    })
  }

  static objectReferencesCustomField(value: unknown, tokens: string[]): boolean {
    if (typeof value === 'string') {
      return DolfinRecord.stringReferencesCustomField(value, tokens)
    }

    if (Array.isArray(value)) {
      return value.some(item => DolfinRecord.objectReferencesCustomField(item, tokens))
    }

    if (value && typeof value === 'object') {
      return Object.values(value).some(item =>
        DolfinRecord.objectReferencesCustomField(item, tokens)
      )
    }

    return false
  }

  static getParentSelects(source: RuleBackedSource) {
    switch (source) {
      case 'activity':
      case 'invoice':
        return ['deals(*)']

      case 'line_item':
        return ['invoices!inner(*, deals!inner(*))']

      case 'deal_line_item':
        return ['deals!inner(*)']

      default:
        return []
    }
  }

  static getQueryFieldPath(
    source: Enums<'plan_source'>,
    field: string | RecordFieldReference | null | undefined
  ) {
    if (!field) return null

    if (source === 'kpi' || source === 'custom_record') {
      const normalizedField = normalizeLocalSourceFieldName(field, source)

      if (!normalizedField) {
        return null
      }

      if (normalizedField.startsWith('$')) {
        return `meta->>${normalizedField.slice(1)}`
      }

      return normalizedField.includes('.') ? null : normalizedField
    }

    const reference = isRecordFieldReference(field)
      ? field
      : resolveRecordFieldReference(field, source)
    const fieldPath = reference.isMeta ? `meta->>${reference.fieldName}` : reference.fieldName

    switch (source) {
      case 'deal':
        return reference.prefix === 'deal' ? fieldPath : null

      case 'invoice':
      case 'activity':
        if (reference.prefix === source) return fieldPath
        if (reference.prefix === 'deal') return `deals.${fieldPath}`
        return null

      case 'line_item':
        if (reference.prefix === 'line_item') return fieldPath
        if (reference.prefix === 'invoice') return `invoices.${fieldPath}`
        if (reference.prefix === 'deal') return `invoices.deals.${fieldPath}`
        return null

      case 'deal_line_item':
        if (reference.prefix === 'deal_line_item') return fieldPath
        if (reference.prefix === 'deal') return `deals.${fieldPath}`
        return null

      default:
        return null
    }
  }

  static getDealQueryFieldPath(
    source: Enums<'plan_source'>,
    field: string | RecordFieldReference | null | undefined
  ) {
    if (!field) return null

    if (source === 'kpi' || source === 'custom_record') {
      return null
    }

    const reference = isRecordFieldReference(field)
      ? field
      : resolveRecordFieldReference(field, source)
    const fieldPath = reference.isMeta ? `meta->>${reference.fieldName}` : reference.fieldName

    switch (source) {
      case 'deal':
        return reference.prefix === 'deal' ? fieldPath : null

      case 'invoice':
      case 'activity':
        if (reference.prefix === 'deal') return fieldPath
        return reference.prefix === source
          ? `${source === 'invoice' ? 'invoices' : 'activities'}.${fieldPath}`
          : null

      case 'line_item':
        if (reference.prefix === 'deal') return fieldPath
        if (reference.prefix === 'invoice') return `invoices.${fieldPath}`
        return reference.prefix === 'line_item' ? `invoices.line_items.${fieldPath}` : null

      case 'deal_line_item':
        if (reference.prefix === 'deal') return fieldPath
        return reference.prefix === 'deal_line_item' ? `deal_line_items.${fieldPath}` : null

      default:
        return null
    }
  }

  static getRelationalCustomFieldDescriptor({
    source,
    field,
    definition,
    virtualTableScope = null
  }: RelationalCustomFieldDescriptorParams): RelationalCustomFieldDescriptor | null {
    if (definition.type === 'formula') {
      return null
    }
    let reference: RecordFieldReference

    if (source === 'kpi' || source === 'custom_record') {
      const normalizedField = normalizeLocalSourceFieldName(field, source)

      if (!normalizedField?.startsWith('$')) {
        return null
      }

      reference = {
        prefix: source,
        fieldName: normalizedField.slice(1),
        isMeta: true
      }
    } else {
      reference = isRecordFieldReference(field) ? field : resolveRecordFieldReference(field, source)
    }

    if (!reference.isMeta || reference.fieldName !== definition.name) {
      return null
    }

    let sourceRole: RelationalCustomFieldSourceRole

    switch (source) {
      case 'deal':
      case 'kpi':
      case 'custom_record':
        if (reference.prefix !== source) {
          return null
        }
        sourceRole = 'local'
        break
      case 'invoice':
      case 'activity':
        if (reference.prefix === source) {
          sourceRole = 'local'
        } else if (reference.prefix === 'deal') {
          sourceRole = 'parent_deal'
        } else {
          return null
        }
        break
      case 'line_item':
        if (reference.prefix === 'line_item') {
          sourceRole = 'local'
        } else if (reference.prefix === 'invoice') {
          sourceRole = 'parent_invoice'
        } else if (reference.prefix === 'deal') {
          sourceRole = 'parent_deal'
        } else {
          return null
        }
        break
      case 'deal_line_item':
        if (reference.prefix === 'deal_line_item') {
          sourceRole = 'local'
        } else if (reference.prefix === 'deal') {
          sourceRole = 'parent_deal'
        } else {
          return null
        }
        break
    }

    if (definition.table_name !== DolfinRecord.CUSTOM_FIELD_TABLE_BY_PREFIX[reference.prefix]) {
      return null
    }

    if (source === 'kpi') {
      if (
        virtualTableScope?.kind !== 'kpi' ||
        definition.kpis_table_id !== virtualTableScope.tableId ||
        definition.custom_record_table_id != null
      ) {
        return null
      }
    } else if (source === 'custom_record') {
      if (
        virtualTableScope?.kind !== 'custom_record' ||
        definition.custom_record_table_id !== virtualTableScope.tableId ||
        definition.kpis_table_id != null
      ) {
        return null
      }
    } else if (
      virtualTableScope != null ||
      definition.kpis_table_id != null ||
      definition.custom_record_table_id != null
    ) {
      return null
    }

    return {
      source,
      sourceRole,
      customFieldId: definition.id,
      virtualTableScope
    }
  }

  static getRelationalCustomFieldDescriptorFromMap(
    source: Enums<'plan_source'>,
    field: string | null | undefined,
    customFields: Record<string, Tables<'custom_fields'>>,
    virtualTableScope: VirtualTableScope | null = null
  ) {
    if (!field) return null
    const normalized =
      source === 'kpi' || source === 'custom_record'
        ? normalizeLocalSourceFieldName(field, source)
        : null
    const reference =
      source === 'kpi' || source === 'custom_record'
        ? normalized?.startsWith('$')
          ? { prefix: source, fieldName: normalized.slice(1), isMeta: true }
          : null
        : resolveRecordFieldReference(field, source)
    if (!reference?.isMeta) return null
    const definition =
      customFields[`${reference.prefix}.${reference.fieldName}`] ??
      customFields[reference.fieldName]
    if (!definition) return null
    return DolfinRecord.getRelationalCustomFieldDescriptor({
      source,
      field,
      definition: { ...definition, name: reference.fieldName },
      virtualTableScope
    })
  }

  static getQuerySelects(
    source: RuleBackedSource,
    fields: Array<string | RecordFieldReference | null | undefined>
  ) {
    const references = getRecordFieldReferences(source, fields)

    switch (source) {
      case 'invoice':
      case 'activity':
        return references.some(reference => reference.prefix === 'deal') ? ['deals!inner(*)'] : []

      case 'line_item':
        if (references.some(reference => reference.prefix === 'deal')) {
          return ['invoices!inner(*, deals!inner(*))']
        }

        return references.some(reference => reference.prefix === 'invoice')
          ? ['invoices!inner(*)']
          : []

      case 'deal_line_item':
        return references.some(reference => reference.prefix === 'deal') ? ['deals!inner(*)'] : []

      default:
        return []
    }
  }

  static getMetricValueSourceSelectQuery(source: Enums<'plan_source'>) {
    const dealSelect = '*, split_anchor:deals!split_anchor_id(id, deal_id, name, organization_id)'

    switch (source) {
      case 'deal':
        return `deals!inner(${dealSelect})`
      case 'activity':
        return `activities!inner(*, deals(${dealSelect}))`
      case 'invoice':
        return `invoices!inner(*, deals(${dealSelect}))`
      case 'line_item':
        return `line_items!inner(*, invoices(*, deals(${dealSelect})))`
      case 'deal_line_item':
        return `deal_line_items!inner(*, deals(${dealSelect}))`
      case 'kpi':
        return 'kpis!inner(*)'
      case 'custom_record':
        return 'custom_records!inner(*)'
    }
  }

  static getMetricValueSourceOrganizationPath(source: Enums<'plan_source'>) {
    return `${
      {
        deal: 'deals',
        activity: 'activities',
        invoice: 'invoices',
        line_item: 'line_items',
        deal_line_item: 'deal_line_items',
        kpi: 'kpis',
        custom_record: 'custom_records'
      }[source]
    }.organization_id`
  }

  static sanitizeMetricValueSourceRow(
    source: Enums<'plan_source'>,
    row: Record<string, unknown>,
    organizationId: number
  ) {
    const sanitizeDeal = (value: unknown) => {
      if (!value || typeof value !== 'object') return value

      const deal = value as Record<string, unknown>
      if (deal.organization_id !== organizationId) return null

      const splitAnchor = deal.split_anchor
      return {
        ...deal,
        split_anchor:
          splitAnchor &&
          typeof splitAnchor === 'object' &&
          (splitAnchor as Record<string, unknown>).organization_id === organizationId
            ? splitAnchor
            : null
      }
    }

    if (source === 'deal') {
      return { ...row, deals: sanitizeDeal(row.deals) }
    }

    if (source === 'activity' || source === 'invoice') {
      const relation = source === 'activity' ? 'activities' : 'invoices'
      const value = row[relation]
      if (!value || typeof value !== 'object') return row

      return {
        ...row,
        [relation]: {
          ...(value as Record<string, unknown>),
          deals: sanitizeDeal((value as Record<string, unknown>).deals)
        }
      }
    }

    if (source === 'line_item') {
      const lineItem = row.line_items
      if (!lineItem || typeof lineItem !== 'object') return row

      const invoice = (lineItem as Record<string, unknown>).invoices
      const safeInvoice =
        invoice &&
        typeof invoice === 'object' &&
        (invoice as Record<string, unknown>).organization_id === organizationId
          ? {
              ...(invoice as Record<string, unknown>),
              deals: sanitizeDeal((invoice as Record<string, unknown>).deals)
            }
          : null

      return {
        ...row,
        line_items: { ...(lineItem as Record<string, unknown>), invoices: safeInvoice }
      }
    }

    if (source === 'deal_line_item') {
      const item = row.deal_line_items
      if (!item || typeof item !== 'object') return row
      return {
        ...row,
        deal_line_items: {
          ...(item as Record<string, unknown>),
          deals: sanitizeDeal((item as Record<string, unknown>).deals)
        }
      }
    }

    return row
  }

  static getSourceFromDataChangeReason(reason: DataChangeReason): Enums<'plan_source'> {
    switch (reason) {
      case 'deal_data_changed':
        return 'deal'
      case 'invoice_data_changed':
        return 'invoice'
      case 'activity_data_changed':
        return 'activity'
      case 'kpi_data_changed':
        return 'kpi'
      case 'line_item_data_changed':
        return 'line_item'
      case 'deal_line_item_data_changed':
        return 'deal_line_item'
      case 'custom_record_data_changed':
        return 'custom_record'
    }
  }

  static fromPayoutDataChangeReason(
    payout: PayoutSourceRecordContext | null | undefined,
    reason: DataChangeReason
  ): DolfinRecord | null {
    if (!payout) return null

    switch (DolfinRecord.getSourceFromDataChangeReason(reason)) {
      case 'deal': {
        const record =
          payout.deals ??
          payout.invoices?.deals ??
          payout.activities?.deals ??
          payout.line_items?.invoices?.deals ??
          payout.deal_line_items?.deals ??
          (payout.deal_id != null
            ? ({
                id: payout.deal_id,
                organization_id: payout.organization_id
              } as Deals)
            : null)

        return record ? new DolfinRecord('deal', record as RecordData) : null
      }

      case 'invoice': {
        const record =
          payout.invoices ??
          payout.line_items?.invoices ??
          (payout.invoice_id != null
            ? ({
                id: payout.invoice_id,
                organization_id: payout.organization_id
              } as InvoicesWithDeals)
            : null)

        return record ? new DolfinRecord('invoice', record as RecordData) : null
      }

      case 'activity': {
        const record =
          payout.activities ??
          (payout.activity_id != null
            ? ({
                id: payout.activity_id,
                organization_id: payout.organization_id
              } as ActivitiesWithDeals)
            : null)

        return record ? new DolfinRecord('activity', record as RecordData) : null
      }

      case 'kpi': {
        const record =
          payout.kpis ??
          (payout.kpi_id != null
            ? ({
                id: payout.kpi_id,
                organization_id: payout.organization_id
              } as KpisWithTables)
            : null)

        return record ? new DolfinRecord('kpi', record as RecordData) : null
      }

      case 'line_item': {
        const record =
          payout.line_items ??
          (payout.line_item_id != null
            ? ({
                id: payout.line_item_id,
                organization_id: payout.organization_id
              } as LineItemsWithJoins)
            : null)

        return record ? new DolfinRecord('line_item', record as RecordData) : null
      }

      case 'deal_line_item': {
        const record =
          payout.deal_line_items ??
          (payout.deal_line_item_id != null
            ? ({
                id: payout.deal_line_item_id,
                organization_id: payout.organization_id
              } as DealLineItemsWithJoins)
            : null)
        return record ? new DolfinRecord('deal_line_item', record as RecordData) : null
      }

      case 'custom_record': {
        const record =
          payout.custom_records ??
          (payout.custom_record_id != null
            ? ({
                id: payout.custom_record_id,
                organization_id: payout.organization_id
              } as CustomRecordsWithTables)
            : null)

        return record ? new DolfinRecord('custom_record', record as RecordData) : null
      }
    }
  }

  static getDiscrepancySourceType(
    payout: PayoutSourceRecordContext | null | undefined
  ): DiscrepancySourceType | null {
    if (!payout) return null

    if (payout.deal_id != null) return 'deal'
    if (payout.invoice_id != null) return 'invoice'
    if (payout.activity_id != null) return 'activity'
    if (payout.kpi_id != null) return 'kpi'
    if (payout.line_item_id != null) return 'line_item'
    if (payout.deal_line_item_id != null) return 'deal_line_item'
    if (payout.custom_record_id != null) return 'custom_record'

    return payout.compensation_rules?.source ?? null
  }

  static fromDiscrepancyPayout(
    payout: PayoutSourceRecordContext | null | undefined
  ): DolfinRecord | null {
    if (!payout) return null

    const sourceType = DolfinRecord.getDiscrepancySourceType(payout)

    switch (sourceType) {
      case 'deal': {
        const record =
          payout.deals ??
          (payout.deal_id != null
            ? ({
                id: payout.deal_id,
                organization_id: payout.organization_id
              } as Deals)
            : null)

        return record ? new DolfinRecord('deal', record as RecordData) : null
      }
      case 'invoice': {
        const record =
          payout.invoices ??
          (payout.invoice_id != null
            ? ({
                id: payout.invoice_id,
                organization_id: payout.organization_id
              } as InvoicesWithDeals)
            : null)

        return record ? new DolfinRecord('invoice', record as RecordData) : null
      }
      case 'activity': {
        const record =
          payout.activities ??
          (payout.activity_id != null
            ? ({
                id: payout.activity_id,
                organization_id: payout.organization_id
              } as ActivitiesWithDeals)
            : null)

        return record ? new DolfinRecord('activity', record as RecordData) : null
      }
      case 'kpi': {
        const record =
          payout.kpis ??
          (payout.kpi_id != null
            ? ({
                id: payout.kpi_id,
                organization_id: payout.organization_id
              } as KpisWithTables)
            : null)

        return record ? new DolfinRecord('kpi', record as RecordData) : null
      }
      case 'line_item': {
        const record =
          payout.line_items ??
          (payout.line_item_id != null
            ? ({
                id: payout.line_item_id,
                organization_id: payout.organization_id
              } as LineItemsWithJoins)
            : null)

        return record ? new DolfinRecord('line_item', record as RecordData) : null
      }
      case 'deal_line_item': {
        const record =
          payout.deal_line_items ??
          (payout.deal_line_item_id != null
            ? ({
                id: payout.deal_line_item_id,
                organization_id: payout.organization_id
              } as DealLineItemsWithJoins)
            : null)
        return record ? new DolfinRecord('deal_line_item', record as RecordData) : null
      }
      case 'custom_record': {
        const record =
          payout.custom_records ??
          (payout.custom_record_id != null
            ? ({
                id: payout.custom_record_id,
                organization_id: payout.organization_id
              } as CustomRecordsWithTables)
            : null)

        return record ? new DolfinRecord('custom_record', record as RecordData) : null
      }
      default:
        return null
    }
  }

  static getDiscrepancyDealInfo(
    payout: PayoutSourceRecordContext | null | undefined
  ): DiscrepancyDealInfo | null {
    return DolfinRecord.fromDiscrepancyPayout(payout)?.getDiscrepancyDealInfo() ?? null
  }

  static getAuditTargetForSource(
    source: Enums<'plan_source'>,
    recordId: number | null | undefined
  ): AuditTarget | null {
    if (recordId == null) {
      return null
    }

    const idColumn = DolfinRecord.getSourceRecordIdColumn(source)
    if (!idColumn) {
      return null
    }

    switch (source) {
      case 'deal':
        return {
          tableName: 'deals_audit_log',
          idColumn,
          recordId
        }
      case 'invoice':
        return {
          tableName: 'invoices_audit_log',
          idColumn,
          recordId
        }
      case 'activity':
        return {
          tableName: 'activities_audit_log',
          idColumn,
          recordId
        }
      case 'kpi':
        return {
          tableName: 'kpis_audit_log',
          idColumn,
          recordId
        }
      case 'line_item':
        return {
          tableName: 'line_items_audit_log',
          idColumn,
          recordId
        }
      case 'deal_line_item':
        return { tableName: 'deal_line_items_audit_log', idColumn, recordId }
      case 'custom_record':
        return {
          tableName: 'custom_records_audit_log',
          idColumn,
          recordId
        }
      default:
        return null
    }
  }

  static getSourceRecordIdColumn(source: Enums<'plan_source'>): SourceRecordIdColumn | null {
    switch (source) {
      case 'deal':
        return 'deal_id'
      case 'invoice':
        return 'invoice_id'
      case 'activity':
        return 'activity_id'
      case 'kpi':
        return 'kpi_id'
      case 'line_item':
        return 'line_item_id'
      case 'deal_line_item':
        return 'deal_line_item_id'
      case 'custom_record':
        return 'custom_record_id'
      default:
        return null
    }
  }

  static getAuditTargetForPayoutContext(
    payout: PayoutSourceRecordContext | null | undefined,
    reason?: DataChangeReason | null
  ): AuditTarget | null {
    return DolfinRecord.getAuditTargetsForPayoutContext(payout, reason)[0] ?? null
  }

  static getAuditTargetsForPayoutContext(
    payout: PayoutSourceRecordContext | null | undefined,
    reason?: DataChangeReason | null
  ): AuditTarget[] {
    if (!payout) return []

    if (reason) {
      const fromReason = DolfinRecord.fromPayoutDataChangeReason(payout, reason)?.getAuditTarget()

      if (fromReason) {
        return [fromReason]
      }
    }

    if (payout.deal_id != null) {
      return [DolfinRecord.getAuditTargetForSource('deal', payout.deal_id)].filter(
        (target): target is AuditTarget => target != null
      )
    }

    if (payout.invoice_id != null) {
      return [DolfinRecord.getAuditTargetForSource('invoice', payout.invoice_id)].filter(
        (target): target is AuditTarget => target != null
      )
    }

    if (payout.activity_id != null) {
      return [DolfinRecord.getAuditTargetForSource('activity', payout.activity_id)].filter(
        (target): target is AuditTarget => target != null
      )
    }

    if (payout.kpi_id != null) {
      return [DolfinRecord.getAuditTargetForSource('kpi', payout.kpi_id)].filter(
        (target): target is AuditTarget => target != null
      )
    }

    if (payout.line_item_id != null) {
      return [DolfinRecord.getAuditTargetForSource('line_item', payout.line_item_id)].filter(
        (target): target is AuditTarget => target != null
      )
    }

    if (payout.deal_line_item_id != null) {
      return [
        DolfinRecord.getAuditTargetForSource('deal_line_item', payout.deal_line_item_id)
      ].filter((target): target is AuditTarget => target != null)
    }

    if (payout.custom_record_id != null) {
      return [
        DolfinRecord.getAuditTargetForSource('custom_record', payout.custom_record_id)
      ].filter((target): target is AuditTarget => target != null)
    }

    const fallbackSource =
      (reason ? DolfinRecord.getSourceFromDataChangeReason(reason) : null) ??
      payout.compensation_rules?.source ??
      null

    if (
      fallbackSource &&
      fallbackSource !== 'kpi' &&
      fallbackSource !== 'custom_record' &&
      Array.isArray(payout.entities_ids) &&
      payout.entities_ids.length > 0
    ) {
      return payout.entities_ids
        .map(recordId => DolfinRecord.getAuditTargetForSource(fallbackSource, Number(recordId)))
        .filter((target): target is AuditTarget => target != null)
    }

    return []
  }

  constructor(
    public readonly type: Enums<'plan_source'>,
    public readonly record: RecordData
  ) {} // TODO: Split DolfinRecord into different classes for each type

  getFieldsRead(): CalculationSnapshotSourceField[] {
    return Array.from(this.fieldsRead.values()).sort((left, right) =>
      left.field.localeCompare(right.field)
    )
  }

  getParentDeal() {
    if (this.type === 'activity' || this.type === 'invoice') {
      return this.recordIsActivity(this.record)
        ? (this.record as ActivitiesWithDeals).deals
        : (this.record as InvoicesWithDeals).deals
    }

    if (this.type === 'line_item') {
      const parentInvoice = (this.record as LineItemsWithJoins).invoices
      if (!parentInvoice) {
        throw new Error('Missing parent invoice', {
          cause: 'NO_INVOICES_FOUND'
        })
      }

      return parentInvoice.deals
    }

    if (this.type === 'deal_line_item') {
      return (this.record as DealLineItemsWithJoins).deals
    }
  }

  getParentInvoice() {
    if (this.type === 'line_item') {
      const parentInvoice = (this.record as LineItemsWithJoins).invoices
      if (!parentInvoice) {
        throw new Error('Missing parent invoice', {
          cause: 'NO_INVOICES_FOUND'
        })
      }
      return parentInvoice
    }
  }

  getPaidInvoice() {
    if (this.type === 'invoice') {
      return this.record as InvoicesWithDeals
    }

    if (this.type === 'line_item') {
      return this.getParentInvoice()
    }

    return null
  }

  getColumnNames() {
    const columns = Object.keys(this.record)

    if (this.type !== 'deal' && this.type !== 'kpi' && this.type !== 'custom_record') {
      const dealColumns = this.getParentDeal()
      const invoicesColumns = this.getParentInvoice()

      columns.push(
        ...Object.keys(dealColumns ?? {}).map(c => `deal.${c}`),
        ...Object.keys(invoicesColumns ?? {}).map(c => `invoice.${c}`)
      )
    }

    return columns
  }

  getDealId() {
    return this.type === 'deal' ? this.record.id : undefined
  }

  getKpiId() {
    return this.type === 'kpi' ? this.record.id : undefined
  }

  getActivityId() {
    return this.type === 'activity' ? this.record.id : undefined
  }

  getInvoiceId() {
    return this.type === 'invoice' ? this.record.id : undefined
  }

  getLineItemId() {
    return this.type === 'line_item' ? this.record.id : undefined
  }

  getDealLineItemId() {
    return this.type === 'deal_line_item' ? this.record.id : undefined
  }

  getCustomRecordId() {
    return this.type === 'custom_record' ? this.record.id : undefined
  }

  getAuditTarget(): AuditTarget | null {
    return DolfinRecord.getAuditTargetForSource(
      this.type,
      typeof this.record.id === 'number' ? this.record.id : null
    )
  }

  getDiscrepancySourceType(): DiscrepancySourceType {
    return this.type
  }

  getDiscrepancyDealInfo(): DiscrepancyDealInfo | null {
    switch (this.type) {
      case 'deal':
        return this.record as DiscrepancyDealInfo
      case 'invoice':
      case 'activity':
        return ((this.record as InvoicesWithDeals | ActivitiesWithDeals).deals ??
          null) as DiscrepancyDealInfo | null
      case 'line_item':
        return ((this.record as LineItemsWithJoins).invoices?.deals ??
          null) as DiscrepancyDealInfo | null
      case 'deal_line_item':
        return ((this.record as DealLineItemsWithJoins).deals ?? null) as DiscrepancyDealInfo | null
      default:
        return null
    }
  }

  getInvoiceExchangeRate() {
    return this.type === 'invoice'
      ? ((this.record as InvoicesWithDeals).exchange_rate ?? undefined)
      : this.type === 'line_item'
        ? ((this.record as LineItemsWithJoins).invoices?.exchange_rate ?? undefined)
        : undefined
  }

  getOrganizationId() {
    return this.type === 'activity' || this.type === 'invoice'
      ? (this.record as ActivitiesWithDeals | InvoicesWithDeals).deals!.organization_id
      : this.record.organization_id
  }

  getDealOwnerId() {
    return this.type === 'activity' || this.type === 'invoice'
      ? (this.record as ActivitiesWithDeals | InvoicesWithDeals).deals!.owner_id
      : this.type === 'kpi'
        ? (this.record as KpisWithTables).profile_id
        : this.type === 'custom_record'
          ? (this.record as CustomRecordsWithTables).owner_id
          : this.type === 'line_item'
            ? (this.record as LineItemsWithJoins).invoices?.deals?.owner_id
            : this.type === 'deal_line_item'
              ? (this.record as DealLineItemsWithJoins).deals?.owner_id
              : (this.record as Deals).owner_id
  }

  getBusinessIdentifier() {
    if (this.type === 'deal') {
      return asDisplayString(this.getFieldValue('deal_id'))
    }

    if (this.type === 'invoice') {
      return asDisplayString(this.getFieldValue('invoice_number'))
    }

    if (this.type === 'line_item') {
      return asDisplayString(this.getFieldValue('id'))
    }

    if (this.type === 'deal_line_item') {
      return asDisplayString(this.getFieldValue('line_item_id'))
    }

    if (this.type === 'custom_record') {
      return asDisplayString(this.getFieldValue('record_id'))
    }

    return asDisplayString(this.getFieldValue('id'))
  }

  getParentDealBusinessIdentifier() {
    const parentDeal = this.getParentDeal()

    if (!parentDeal) {
      return null
    }

    return asDisplayString(new DolfinRecord('deal', parentDeal).getFieldValue('deal_id'))
  }

  getParentDealDisplayLabel() {
    const parentDeal = this.getParentDeal()

    if (!parentDeal) {
      return null
    }

    const dealRecord = new DolfinRecord('deal', parentDeal)
    const dealId = asDisplayString(dealRecord.getFieldValue('deal_id'))
    const dealName = asDisplayString(dealRecord.getFieldValue('name'))

    if (dealId && dealName) {
      return `${dealId} - ${dealName}`
    }

    return dealId ?? dealName
  }

  getDisplayLabel(options: RecordDisplayLabelOptions = {}) {
    if (this.type === 'deal') {
      const dealId = this.getBusinessIdentifier()
      const dealName = asDisplayString(this.getFieldValue('name'))
      if (dealName) return dealId ? `${dealName} #${dealId}` : dealName
      return dealId
        ? `deal ${dealId}`
        : options.fallbackId != null
          ? `deal #${options.fallbackId}`
          : 'deal'
    }

    if (this.type === 'invoice') {
      const invoiceNumber = this.getBusinessIdentifier()
      const dealLabel = this.getParentDealDisplayLabel()

      if (invoiceNumber && dealLabel) {
        return `invoice ${invoiceNumber} · deal ${dealLabel}`
      }

      if (invoiceNumber) {
        return `invoice ${invoiceNumber}`
      }

      return options.fallbackId != null ? `invoice #${options.fallbackId}` : 'invoice'
    }

    if (this.type === 'activity') {
      const dealLabel = this.getParentDealDisplayLabel()
      const activityType = asDisplayString(this.getFieldValue('type'))

      if (dealLabel && activityType) {
        return `deal ${dealLabel} · ${activityType}`
      }

      if (dealLabel) {
        return `deal ${dealLabel} · activity`
      }

      if (activityType) {
        return `activity · ${activityType}`
      }

      return options.fallbackId != null ? `activity #${options.fallbackId}` : 'activity'
    }

    if (this.type === 'kpi') {
      const periodStart = asDisplayString(this.getFieldValue('period_start'))
      const profileId = asDisplayString(this.getFieldValue('profile_id'))
      const profileLabel = profileId ? (options.getProfileLabel?.(profileId) ?? profileId) : null

      if (periodStart && profileLabel) {
        return `${periodStart} · ${profileLabel}`
      }

      if (periodStart) {
        return periodStart
      }

      return profileLabel
        ? `KPI · ${profileLabel}`
        : options.fallbackId != null
          ? `kpi #${options.fallbackId}`
          : 'kpi'
    }

    if (this.type === 'line_item') {
      const productName =
        asDisplayString(this.getFieldValue('product_name')) ??
        asDisplayString(this.getFieldValue('description'))
      let parentInvoice: InvoicesWithDeals | null = null
      try {
        parentInvoice = this.getParentInvoice() ?? null
      } catch {
        parentInvoice = null
      }
      const invoiceNumber = parentInvoice
        ? asDisplayString(
            new DolfinRecord('invoice', parentInvoice).getFieldValue('invoice_number')
          )
        : null
      const dealLabel = this.getParentDealDisplayLabel()

      if (productName && invoiceNumber && dealLabel) {
        return `line item ${productName} · invoice ${invoiceNumber} · deal ${dealLabel}`
      }

      if (productName && invoiceNumber) {
        return `line item ${productName} · invoice ${invoiceNumber}`
      }

      return options.fallbackId != null ? `line item #${options.fallbackId}` : 'line item'
    }

    if (this.type === 'deal_line_item') {
      const productName =
        asDisplayString(this.getFieldValue('product_name')) ??
        asDisplayString(this.getFieldValue('description'))
      const dealLabel = this.getParentDealDisplayLabel()
      if (productName && dealLabel) return `deal line item ${productName} · deal ${dealLabel}`
      if (productName) return `deal line item ${productName}`
      return options.fallbackId != null ? `deal line item #${options.fallbackId}` : 'deal line item'
    }

    if (this.type === 'custom_record') {
      const recordId = this.getBusinessIdentifier()
      const ownerId = asDisplayString(this.getFieldValue('owner_id'))
      const ownerLabel = ownerId ? (options.getProfileLabel?.(ownerId) ?? ownerId) : null

      if (recordId && ownerLabel) {
        return `custom record ${recordId} · ${ownerLabel}`
      }

      if (recordId) {
        return `custom record ${recordId}`
      }

      return options.fallbackId != null ? `custom record #${options.fallbackId}` : 'custom record'
    }

    return options.fallbackId != null ? `${this.type} #${options.fallbackId}` : this.type
  }

  getProcessingDate(customDateField?: string | null, customFields?: CustomFieldsMap) {
    const columnName = customDateField
      ? customDateField
      : DolfinRecord.DATE_COLUMN_BY_SOURCE[this.type]

    const processingDate = this.getFieldValue(columnName, customFields) as string | null
    if (processingDate && isValidDate(processingDate)) {
      return processingDate
    }

    return null
  }

  metaIsObject(meta: unknown): meta is MetaObject {
    return (
      meta !== null &&
      typeof meta === 'object' &&
      !Array.isArray(meta) &&
      Object.prototype.hasOwnProperty.call(meta, 'value') &&
      DolfinRecord.isRecordScalar((meta as MetaObject).value)
    )
  }

  recordIsActivity(record: RecordData): record is ActivitiesWithDeals {
    return this.type === 'activity'
  }

  recordIsInvoice(record: RecordData): record is InvoicesWithDeals {
    return this.type === 'invoice'
  }

  recordIsLineItem(record: RecordData): record is LineItemsWithJoins {
    return this.type === 'line_item'
  }

  recordIsDealLineItem(record: RecordData): record is DealLineItemsWithJoins {
    return this.type === 'deal_line_item'
  }

  recordHasParentDeal(
    record: RecordData
  ): record is
    | ActivitiesWithDeals
    | InvoicesWithDeals
    | LineItemsWithJoins
    | DealLineItemsWithJoins {
    return (
      this.type === 'invoice' ||
      this.type === 'activity' ||
      this.type === 'line_item' ||
      this.type === 'deal_line_item'
    )
  }

  recordIsDeal(record: RecordData): record is Deals {
    return this.type === 'deal'
  }

  getFieldValue(fieldName: string | RecordFieldReference, customFields?: CustomFieldsMap) {
    const normalizedFieldName =
      this.type === 'kpi' || this.type === 'custom_record'
        ? normalizeLocalSourceFieldName(fieldName, this.type)
        : stringifyRecordFieldReference(this.type, fieldName)

    if (!normalizedFieldName) {
      return null
    }

    if (
      this.type === 'deal_line_item' &&
      !DolfinRecord.getQueryFieldPath(this.type, normalizedFieldName)
    ) {
      return null
    }

    const parentTable = normalizedFieldName.startsWith('deal.')
      ? 'deals'
      : normalizedFieldName.startsWith('invoice.')
        ? 'invoices'
        : undefined
    const cleanedUpName = normalizedFieldName.replace(/^(deal|invoice)\./, '').replace(/^\$/, '')

    const value =
      this.getColumnValue(
        parentTable
          ? normalizedFieldName.startsWith('deal.$') || normalizedFieldName.startsWith('invoice.$')
          : normalizedFieldName.startsWith('$'),
        cleanedUpName,
        parentTable,
        customFields
      ) ?? null

    const customFieldName = normalizedFieldName.replace(/\$/g, '')
    const localCustomFieldName = customFieldName.replace(/^(deal|invoice|custom_record)\./, '')
    const customField =
      customFields?.[customFieldName] ??
      customFields?.[`${this.type}.${localCustomFieldName}`] ??
      customFields?.[localCustomFieldName]
    const currencyReference = getRecordFieldCurrencyReference(this.type, normalizedFieldName)
    const currencyParentTable =
      currencyReference?.prefix === 'deal' && this.type !== 'deal'
        ? 'deals'
        : currencyReference?.prefix === 'invoice' && this.type !== 'invoice'
          ? 'invoices'
          : undefined
    const currency = currencyReference
      ? this.getColumnValue(false, currencyReference.fieldName, currencyParentTable)
      : undefined
    this.fieldsRead.set(normalizedFieldName, {
      field: normalizedFieldName,
      // Snapshot evidence must be JSON-safe, even when a formula evaluates to NaN/Infinity.
      value: typeof value === 'number' && !Number.isFinite(value) ? null : value,
      label: customField?.label ?? normalizedFieldName,
      type: currencyReference ? 'monetary' : (customField?.type ?? 'native'),
      ...(typeof currency === 'string' ? { currency } : {})
    })
    return value
  }

  getFormulaValue(record: DolfinRecord, column: string, customFields: CustomFieldsMap) {
    const dolfinDSL = new DolfinDSL({
      recordData: record,
      formula: customFields[column].formula!,
      customFields,
      inFormula: true
    })

    try {
      const pFormula = dolfinDSL.parse()
      const value = new Function(`return ${pFormula}`)()
      if (record !== this) {
        const parentPrefix = column.startsWith('deal.')
          ? 'deal.'
          : column.startsWith('invoice.')
            ? 'invoice.'
            : ''
        record.getFieldsRead().forEach(field => {
          const fieldName = /^(deal|invoice)\./.test(field.field)
            ? field.field
            : `${parentPrefix}${field.field}`
          this.fieldsRead.set(fieldName, { ...field, field: fieldName })
        })
      }
      return value
    } catch (e) {
      console.error('Failed to evaluate formula', customFields[column].formula, e)
      return null
    }
  }

  getCurrency() {
    return (
      (this.recordIsInvoice(this.record) ||
        this.recordIsDeal(this.record) ||
        this.recordIsLineItem(this.record) ||
        this.recordIsDealLineItem(this.record)) &&
      (this.record.currency ??
        (this.recordIsDealLineItem(this.record) ? this.record.deals?.currency : null))
    )
  }

  getColumnValue(
    isCustomField: boolean,
    column: string,
    parentTable?: 'deals' | 'invoices',
    customFields?: CustomFieldsMap
  ): number | string | null | boolean | string[] {
    if (
      this.recordIsActivity(this.record) ||
      this.recordIsInvoice(this.record) ||
      this.recordIsLineItem(this.record) ||
      this.recordIsDealLineItem(this.record)
    ) {
      const parentDeal = this.getParentDeal()
      const parentInvoice = this.getParentInvoice()

      if (
        (parentTable === 'deals' && !parentDeal) ||
        (parentTable === 'invoices' && !parentInvoice)
      ) {
        throw new Error('Missing parent deal or invoice', {
          cause: 'NO_DEALS_FOUND'
        })
      }

      if (isCustomField) {
        const meta =
          parentTable === 'deals'
            ? (parentDeal!.meta as RecordMeta | undefined)?.[column]
            : parentTable === 'invoices'
              ? (parentInvoice!.meta as RecordMeta | undefined)?.[column]
              : (this.record.meta as RecordMeta | undefined)?.[column]
        const columnWithPrefix = `${
          parentTable ? `${parentTable === 'invoices' ? 'invoice' : 'deal'}.` : ''
        }${column}`

        if (customFields && customFields[columnWithPrefix]?.type === 'formula') {
          return this.getFormulaValue(
            parentTable === 'invoices'
              ? new DolfinRecord('invoice', parentInvoice!)
              : parentTable === 'deals'
                ? new DolfinRecord('deal', parentDeal!)
                : this,
            columnWithPrefix,
            customFields
          )
        }

        return this.metaIsObject(meta) ? meta.value : (meta as RecordScalar)
      }

      if (parentTable) {
        return (
          (parentTable === 'deals'
            ? parentDeal![
                column as keyof Omit<
                  Deals,
                  'meta' | 'meta_with_defaults' | 'frozen_columns' | 'split_payload'
                >
              ]
            : parentInvoice![
                column as keyof Omit<
                  InvoicesWithDeals,
                  'meta' | 'meta_with_defaults' | 'frozen_columns' | 'deals'
                >
              ]) ?? null
        )
      }

      if (this.recordIsInvoice(this.record)) {
        return (
          this.record[
            column as keyof Omit<
              Tables<'invoices'>,
              'meta' | 'meta_with_defaults' | 'frozen_columns' | 'invoices_search_columns'
            >
          ] ?? null
        )
      }

      if (this.recordIsDealLineItem(this.record) && column === 'currency') {
        return this.record.currency ?? parentDeal?.currency ?? null
      }

      return (
        this.record[
          column as keyof Omit<
            Tables<'activities'> | Tables<'line_items'> | Tables<'deal_line_items'>,
            'meta' | 'meta_with_defaults'
          >
        ] ?? null
      )
    }

    if (isCustomField) {
      const field = customFields?.[column]
      if (field?.type === 'formula') {
        return this.parseFormula(column, field.formula!, customFields!)
      }
      const meta = (this.record.meta as RecordMeta | undefined)?.[column]
      return this.metaIsObject(meta) ? meta.value : (meta as RecordScalar)
    }

    return this.record[
      column as keyof Omit<
        Deals | KpisWithTables | CustomRecordsWithTables,
        'meta' | 'meta_with_defaults' | 'frozen_columns' | 'custom_record_tables'
      >
    ]
  }

  isDateColumn(column: string) {
    if (this.type === 'activity') {
      return [
        'activity_date',
        'created_at',
        'updated_at',
        'deal.created_at',
        'deal.close_date',
        'deal.updated_at'
      ].includes(column)
    }

    if (this.type === 'invoice') {
      return [
        'created_at',
        'due_date',
        'effective_at',
        'payment_date',
        'updated_at',
        'deal.created_at',
        'deal.close_date',
        'deal.updated_at'
      ].includes(column)
    }

    if (this.type === 'line_item') {
      return [
        'created_at',
        'updated_at',
        'invoice.due_date',
        'invoice.effective_at',
        'invoice.payment_date',
        'deal.created_at',
        'deal.close_date',
        'deal.updated_at'
      ].includes(column)
    }

    if (this.type === 'deal_line_item') {
      return [
        'created_at',
        'updated_at',
        'service_date',
        'billing_start_date',
        'billing_end_date',
        'deal.created_at',
        'deal.close_date',
        'deal.updated_at'
      ].includes(column)
    }

    if (this.type === 'custom_record') {
      return ['created_at', 'updated_at'].includes(column)
    }

    return ['created_at', 'close_date', 'updated_at'].includes(column)
  }

  private parseFormula(property: string, formula: string, customFields: CustomFieldsMap) {
    const parser = new DolfinDSL({
      formula,
      recordData: this,
      metaColumnsCache: [property],
      customFields,
      inFormula: true
    })

    const parsedFormula = parser.parse()

    return new Function(`return ${parsedFormula}`)()
  }
}

/**
 * Produce the necessary parent join/select fragments for querying records of the given plan source.
 *
 * @param source - The rule-backed plan source (e.g., 'deal', 'invoice', 'activity', 'line_item') used to determine which parent selects are required
 * @returns An array of select strings to include in a query (for example `['deals(*)']` or `['invoices!inner(*, deals!inner(*))']`)
 */
export function getRecordParentSelects(source: RuleBackedSource) {
  return DolfinRecord.getParentSelects(source)
}

function getRecordFieldReferences(
  source: Enums<'plan_source'>,
  fields: Array<string | RecordFieldReference | null | undefined>
) {
  return fields
    .map(field => {
      if (field == null) {
        return null
      }

      if (isRecordFieldReference(field)) {
        return field
      }

      if (source === 'kpi') {
        return null
      }

      return resolveRecordFieldReference(field, source)
    })
    .filter((field): field is RecordFieldReference => field != null)
}

/**
 * Build the default `select` clause for loading records by source, including required parent joins.
 */
export function getRecordSourceSelectQuery(source: Enums<'plan_source'>) {
  if (source === 'deal' || source === 'kpi' || source === 'custom_record') {
    return '*'
  }

  const parentSelects = DolfinRecord.getQuerySelects(source, ['deal.id'])
  return ['*', ...parentSelects].join(', ')
}

export function getMetricValueSourceSelectQuery(source: Enums<'plan_source'>) {
  return DolfinRecord.getMetricValueSourceSelectQuery(source)
}

export function getMetricValueSourceOrganizationPath(source: Enums<'plan_source'>) {
  return DolfinRecord.getMetricValueSourceOrganizationPath(source)
}

export function sanitizeMetricValueSourceRow(
  source: Enums<'plan_source'>,
  row: Record<string, unknown>,
  organizationId: number
) {
  return DolfinRecord.sanitizeMetricValueSourceRow(source, row, organizationId)
}

export function getRecordIdSelectQuery(
  source: Enums<'plan_source'>,
  fields: Array<string | RecordFieldReference | null | undefined>
) {
  const references = getRecordFieldReferences(source, fields)

  switch (source) {
    case 'invoice':
    case 'activity':
      return references.some(reference => reference.prefix === 'deal')
        ? 'id, deals!inner(id)'
        : 'id'

    case 'line_item':
      if (references.some(reference => reference.prefix === 'deal')) {
        return 'id, invoices!inner(id, deals!inner(id))'
      }

      return references.some(reference => reference.prefix === 'invoice')
        ? 'id, invoices!inner(id)'
        : 'id'

    case 'deal_line_item':
      return references.some(reference => reference.prefix === 'deal')
        ? 'id, deals!inner(id)'
        : 'id'

    default:
      return 'id'
  }
}

/**
 * Convert a field identifier into the PostgREST-friendly field path required for queries against `source`.
 *
 * @param source - The record source context (e.g., `deal`, `invoice`, `line_item`, `activity`)
 * @param field - A field name, a resolved `RecordFieldReference`, or `null`/`undefined` to indicate no field
 * @returns The source-specific query field path (for example `meta->>column`, `deals.column`, `invoices.deals.column`) or `null` when the input does not map to a selectable path
 */
export function getRecordQueryFieldPath(
  source: Enums<'plan_source'>,
  field: string | RecordFieldReference | null | undefined
) {
  return DolfinRecord.getQueryFieldPath(source, field)
}

export function getRelationalCustomFieldDescriptor(params: RelationalCustomFieldDescriptorParams) {
  return DolfinRecord.getRelationalCustomFieldDescriptor(params)
}

/**
 * Determine which parent-table select/join clauses are required for querying the given fields.
 *
 * @param source - The plan source (e.g., 'deal', 'invoice', 'line_item', 'activity') against which fields are resolved
 * @param fields - Field paths or references to inspect for cross-table dependencies
 * @returns An array of select/join strings to include in the query (for example `deals!inner(*)` or `invoices!inner(*, deals!inner(*))`), or an empty array if no parent selects are needed
 */
export function getRecordQuerySelects(
  source: RuleBackedSource,
  fields: Array<string | RecordFieldReference | null | undefined>
) {
  return DolfinRecord.getQuerySelects(source, fields)
}

export function getRecordDisplayLabel(
  source: Enums<'plan_source'>,
  record: RecordData,
  options?: RecordDisplayLabelOptions
) {
  return new DolfinRecord(source, record).getDisplayLabel(options)
}

export default DolfinRecord
